import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  DollarSign, Clock, CheckCircle, Upload, Search, X,
  ChevronUp, ChevronDown, FileText, AlertCircle, AlertTriangle,
  ArrowRight, RefreshCw, Plus, Trash2, Edit2, Save,
  Building2, Users, TrendingUp, BarChart3, Flag,
  ChevronLeft, ChevronRight as ChevronRightIcon, Globe, User,
  Download, Printer, ArrowLeft, ExternalLink, Calendar, Filter, SlidersHorizontal, Image, Database
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import WeeklySummaryModule from "./WeeklySummaryModule";
import PhaseCBanner from "./PhaseCBanner";
import InspectorPayrollReportModule from "./InspectorPayrollReportModule";
import ExpenseCategoriesModule from "./ExpenseCategoriesModule";
import VendorsModule from "./VendorsModule";
import BillsModule from "./BillsModule";
import CashMovementsModule from "./CashMovementsModule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const FinancePage = ({ Layout }) => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState("orders");

  // P1-3 (Angela 2026-05-17) — Payroll Dedup banner. Pulses red when the
  // background monitor flagged orders with >1 paid daily_closing.
  const [payrollDedupCount, setPayrollDedupCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/finance/payroll-dedup-count`);
        if (!cancelled) setPayrollDedupCount(r.data?.count || 0);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const modules = [
    { key: "orders", label: "Orders", icon: FileText },
    { key: "reconciliation", label: "Reconciliation", icon: CheckCircle },
    { key: "payments", label: "Payments", icon: DollarSign },
    { key: "followup", label: "Send Follow-Up", icon: AlertCircle },
    { key: "rates", label: "Safeguard Rates", icon: Building2 },
    { key: "inspector_rates", label: "Inspector Rates", icon: Users },
    { key: "national", label: "National", icon: Globe },
    { key: "transmission", label: "Transmission Payment", icon: ArrowRight },
    { key: "operational", label: "Operational Payment", icon: Users },
    { key: "expenses", label: "Office Expenses", icon: DollarSign },
    { key: "pj", label: "Office (PJ)", icon: Users },
    { key: "summary", label: "Weekly Summary", icon: BarChart3 },
    { key: "payroll_report", label: "Payroll & Profit", icon: TrendingUp },
    { key: "expense_categories", label: "Categories", icon: FileText },
    { key: "vendors", label: "Vendors", icon: Users },
    { key: "bills", label: "Bills", icon: DollarSign },
    { key: "cash_movements", label: "Cash Movements", icon: ArrowRight },
  ];

  return (
    <Layout>
      <div className="space-y-5" data-testid="finance-page">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Finance</h1>
          <p className="text-gray-500 text-sm">Weekly closing, reconciliation, and payment tracking</p>
        </div>

        <PhaseCBanner department="Finance" testIdPrefix="finance" />

        {/* P1-3 (Angela 2026-05-17) — PAYROLL DEDUP BANNER. Pulses red
            when daily background monitor (06:00 NY) detected orders with
            >1 paid daily_closing. Click opens the alerts module. */}
        {payrollDedupCount > 0 && (
          <button
            onClick={() => setActiveModule('payroll_dedup')}
            className={`relative w-full p-4 rounded-[14px] border-2 transition-all text-left bg-[#FCECEA] shadow-[0_1px_4px_rgba(0,0,0,0.06)] ${
              activeModule === 'payroll_dedup'
                ? 'border-[#B5302A] ring-2 ring-[#B5302A33]'
                : 'border-[#B5302A33] hover:border-[#B5302A] animate-pulse'
            }`}
            data-testid="finance-payroll-dedup-banner"
          >
            <span className="absolute -top-1.5 -right-1.5 flex h-6 w-6">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-6 w-6 bg-[#B5302A] text-white text-[11px] font-black items-center justify-center">{payrollDedupCount}</span>
            </span>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-[#B5302A] shrink-0" />
              <span className="text-[10px] sm:text-xs font-extrabold text-[#B5302A] uppercase tracking-[0.08em]">Payroll Dedup — duplicate payments detected</span>
            </div>
            <p className="text-[11px] sm:text-xs text-[#9C2A24] mt-1">
              <span className="hidden sm:inline">Orders with more than 1 paid closing — daily monitor flagged these for review. No payments were modified automatically. </span>
              <span className="font-semibold">Click to review and resolve.</span>
            </p>
          </button>
        )}

        <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-200 flex-wrap" data-testid="finance-tabs">
          {modules.map(m => {
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => setActiveModule(m.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                  activeModule === m.key
                    ? "bg-[#D1A054] text-[#162d4a]"
                    : "text-gray-500 hover:text-[#1F2937] hover:bg-gray-100"
                }`}
                data-testid={`finance-tab-${m.key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {m.label}
              </button>
            );
          })}
        </div>

        {activeModule === "orders" && <OrdersModule />}
        {activeModule === "reconciliation" && <ReconciliationModule />}
        {activeModule === "payments" && <PaymentsModule />}
        {activeModule === "followup" && <SendFollowUpModule />}
        {activeModule === "rates" && <SafeguardRatesModule />}
        {activeModule === "inspector_rates" && <InspectorRatesModule />}
        {activeModule === "national" && <NationalModule />}
        {activeModule === "transmission" && <TransmissionPaymentModule />}
        {activeModule === "operational" && <OperationalPaymentModule />}
        {activeModule === "expenses" && <OfficeExpensesModule />}
        {activeModule === "pj" && <PJPaymentModule />}
        {activeModule === "summary" && <WeeklySummaryModule />}
        {activeModule === "payroll_report" && <InspectorPayrollReportModule />}
        {activeModule === "expense_categories" && <ExpenseCategoriesModule />}
        {activeModule === "vendors" && <VendorsModule />}
        {activeModule === "bills" && <BillsModule />}
        {activeModule === "cash_movements" && <CashMovementsModule />}
        {activeModule === "payroll_dedup" && <PayrollDedupModule />}
      </div>
    </Layout>
  );
};

/* ── P1-3 (Angela 2026-05-17) — Payroll Dedup Module ── */
const PayrollDedupModule = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finance/payroll-dedup-alerts${showResolved ? '?resolved=true' : ''}`);
      setItems(r.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleResolve = async (orderNumber) => {
    const note = window.prompt('Resolution note (optional):') || '';
    try {
      await axios.post(`${API}/finance/payroll-dedup-alerts/${orderNumber}/resolve`, { note });
      await fetchAlerts();
    } catch (e) {
      alert('Failed to resolve: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleScan = async () => {
    try {
      const r = await axios.post(`${API}/finance/payroll-dedup-scan`);
      alert(`Scan ${r.data.scan_id}: ${r.data.new_alerts} new, ${r.data.duplicate_alerts} already-flagged.`);
      await fetchAlerts();
    } catch (e) {
      alert('Scan failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="bg-white rounded-[14px] border border-gray-200 p-4 shadow-[0_1px_4px_rgba(0,0,0,0.06)]" data-testid="payroll-dedup-module">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-bold text-gray-800">Payroll Dedup Alerts</h2>
          <p className="text-xs text-gray-500">Orders with more than 1 paid daily_closing. Detect-only — payments are never auto-modified.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowResolved(s => !s)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 hover:bg-gray-50"
            data-testid="payroll-dedup-toggle-resolved"
          >
            {showResolved ? 'Show open' : 'Show resolved'}
          </button>
          <button
            onClick={handleScan}
            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-[#0F1B2D] text-white hover:opacity-90"
            data-testid="payroll-dedup-scan-now"
          >
            Scan now
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="payroll-dedup-empty">No {showResolved ? 'resolved' : 'open'} alerts.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="payroll-dedup-table">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Inspectors</th>
                <th className="px-3 py-2 text-center">Closings</th>
                <th className="px-3 py-2 text-right">Total Paid</th>
                <th className="px-3 py-2 text-left">Detected</th>
                {showResolved && <th className="px-3 py-2 text-left">Resolved</th>}
                {!showResolved && <th className="px-3 py-2 text-center">Action</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.order_number + (item.scan_id || '')} className="border-t border-gray-100" data-testid={`payroll-dedup-row-${item.order_number}`}>
                  <td className="px-3 py-2 font-mono text-xs font-bold">{item.order_number}</td>
                  <td className="px-3 py-2">{(item.inspectors || []).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-center font-bold text-[#B5302A]">{item.count}</td>
                  <td className="px-3 py-2 text-right font-bold text-[#B5302A]">${(item.total_paid || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{(item.detected_at || '').slice(0,10)}</td>
                  {showResolved && (
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {item.resolved_by} <br/><span className="text-[10px]">{(item.resolved_at || '').slice(0,10)}</span>
                    </td>
                  )}
                  {!showResolved && (
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleResolve(item.order_number)}
                        className="px-2 py-1 text-[10px] font-bold rounded bg-[#1E6B45] text-white hover:opacity-90"
                        data-testid={`payroll-dedup-resolve-${item.order_number}`}
                      >
                        Resolve
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Shared: Week Period Generator (SINGLE SOURCE OF TRUTH) ── */
/* HARD LOCK: Tuesday → Monday. Uses local date formatting to avoid UTC timezone shift. */
const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const EXCLUDED_WEEKS = new Set(["2026-03-24", "2026-03-17", "2026-03-10"]);

const generateWeekPeriods = (count = 4) => {
  const today = new Date();
  const daysSinceTue = (today.getDay() + 5) % 7;
  const periods = [];
  for (let i = 0; i < count + 5; i++) {
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceTue - (i * 7));
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    const startStr = fmtLocalDate(start);
    if (!EXCLUDED_WEEKS.has(startStr)) {
      periods.push({ start: startStr, end: fmtLocalDate(end) });
    }
    if (periods.length >= count) break;
  }
  return periods;
};

/* ════════════════════════════════════════════════════════
   SHARED: WEEK SELECTOR COMPONENT
   ════════════════════════════════════════════════════════ */
const WeekSelector = ({ weeks, selected, onSelect, color = "#2F5D8A" }) => {
  const fmtWeek = (w) => {
    if (!w) return "";
    const s = new Date(w.start + "T12:00:00");
    const e = new Date(w.end + "T12:00:00");
    return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };
  return (
    <div className="flex gap-2 flex-wrap" data-testid="week-selector">
      {weeks.map((w, i) => (
        <button key={i} onClick={() => onSelect(w)}
          className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
            selected?.start === w.start
              ? "text-white border-transparent"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
          }`}
          style={selected?.start === w.start ? { backgroundColor: color, borderColor: color } : {}}
          data-testid={`week-${w.start}`}
        >{fmtWeek(w)}</button>
      ))}
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   ORDERS MODULE — BY CLOSING WEEK
   ════════════════════════════════════════════════════════ */
const OrdersModule = () => {
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weeks] = useState(() => generateWeekPeriods(6));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [cardFilter, setCardFilter] = useState(null);
  const [showCheckDetail, setShowCheckDetail] = useState(false);
  const [showNonPayable, setShowNonPayable] = useState(false);
  const [sortCol, setSortCol] = useState("route_date");
  const [sortDir, setSortDir] = useState("asc");
  const [filterInspector, setFilterInspector] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterInvestigation, setFilterInvestigation] = useState(false);
  // Expand state
  const [expandedOn, setExpandedOn] = useState(null);
  const [expandData, setExpandData] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const [adjPay, setAdjPay] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjMsg, setAdjMsg] = useState("");
  // Inline edit state (independent of expand)
  const [editingOn, setEditingOn] = useState(null);
  const [editPay, setEditPay] = useState("");
  const [editReason, setEditReason] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState("");

  useEffect(() => { if (weeks.length > 0 && !selectedWeek) setSelectedWeek(weeks[0]); }, [weeks, selectedWeek]);

  const fetchOrders = useCallback(async () => {
    if (!selectedWeek) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/orders-by-week`, { params: { week_start: selectedWeek.start, week_end: selectedWeek.end } });
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [selectedWeek]);

  const toggleExpand = async (orderNum) => {
    if (expandedOn === orderNum) {
      setExpandedOn(null); setExpandData(null); return;
    }
    setExpandedOn(orderNum); setExpandData(null); setExpandLoading(true);
    setAdjPay(""); setAdjReason(""); setAdjMsg("");
    try {
      const res = await axios.get(`${API}/finance/order-expand/${orderNum}`);
      setExpandData(res.data);
      if (res.data?.adjustment) {
        setAdjPay(String(res.data.adjustment.adjusted_pay));
        setAdjReason(res.data.adjustment.reason);
      }
    } catch { setExpandData(null); }
    finally { setExpandLoading(false); }
  };

  const saveAdjustment = async (orderRow) => {
    if (!adjReason.trim()) { setAdjMsg("Reason is required."); return; }
    if (!adjPay || isNaN(parseFloat(adjPay))) { setAdjMsg("Enter a valid amount."); return; }
    setAdjSaving(true); setAdjMsg("");
    try {
      await axios.post(`${API}/finance/adjust-inspector-pay`, {
        order_number: orderRow.order_number,
        adjusted_pay: parseFloat(adjPay),
        original_pay: orderRow.original_inspector_pay || orderRow.inspector_pay || 0,
        reason: adjReason.trim(),
        week_start: selectedWeek?.start || "",
        week_end: selectedWeek?.end || "",
      });
      setAdjMsg("Saved!");
      fetchOrders(); // refresh table to show new adjusted value
      // Refresh expand data
      const res = await axios.get(`${API}/finance/order-expand/${orderRow.order_number}`);
      setExpandData(res.data);
    } catch (err) { setAdjMsg(err.response?.data?.detail || "Error saving."); }
    finally { setAdjSaving(false); }
  };

  const startInlineEdit = (o, e) => {
    e.stopPropagation();
    setEditingOn(o.order_number);
    setEditPay(o.has_adjustment ? String(o.inspector_pay) : "");
    setEditReason(o.adjustment?.reason || "");
    setEditMsg("");
  };

  const cancelInlineEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingOn(null); setEditPay(""); setEditReason(""); setEditMsg("");
  };

  const saveInlineEdit = async (o, e) => {
    if (e) e.stopPropagation();
    if (!editReason.trim()) { setEditMsg("Reason required"); return; }
    if (!editPay || isNaN(parseFloat(editPay))) { setEditMsg("Valid amount required"); return; }
    setEditSaving(true); setEditMsg("");
    try {
      await axios.post(`${API}/finance/adjust-inspector-pay`, {
        order_number: o.order_number,
        adjusted_pay: parseFloat(editPay),
        original_pay: o.original_inspector_pay || o.inspector_pay || 0,
        reason: editReason.trim(),
        week_start: selectedWeek?.start || "",
        week_end: selectedWeek?.end || "",
      });
      setEditingOn(null); setEditPay(""); setEditReason(""); setEditMsg("");
      fetchOrders();
    } catch (err) { setEditMsg(err.response?.data?.detail || "Error"); }
    finally { setEditSaving(false); }
  };

  useEffect(() => { fetchOrders(); setCardFilter(null); setShowCheckDetail(false); setShowNonPayable(false); setExpandedOn(null); setExpandData(null); setEditingOn(null); }, [fetchOrders]);

  const s = data?.summary || {};

  // Unique values for filters
  const inspectorList = useMemo(() => {
    if (!data?.orders) return [];
    return [...new Set(data.orders.map(o => o.inspector).filter(Boolean))].sort();
  }, [data]);
  const typeList = useMemo(() => {
    if (!data?.orders) return [];
    return [...new Set(data.orders.map(o => o.flow_type).filter(Boolean))].sort();
  }, [data]);
  const dateList = useMemo(() => {
    if (!data?.orders) return [];
    return [...new Set(data.orders.map(o => o.route_date).filter(Boolean))].sort();
  }, [data]);

  const orders = useMemo(() => {
    if (!data?.orders) return [];
    let filtered = data.orders;
    if (cardFilter === "missing") filtered = filtered.filter(o => o.expected > 0 && o.received === 0);
    else if (cardFilter === "underpaid") filtered = filtered.filter(o => o.received > 0 && o.expected > 0 && o.received < o.expected - 0.01);
    else if (cardFilter === "overpaid") filtered = filtered.filter(o => o.received > 0 && o.expected > 0 && o.received > o.expected + 0.01);
    else if (cardFilter === "differences") filtered = filtered.filter(o => o.received > 0 && o.expected > 0 && Math.abs(o.received - o.expected) > 0.01);
    else if (cardFilter === "received") filtered = filtered.filter(o => o.received > 0);
    if (filterInspector) filtered = filtered.filter(o => o.inspector === filterInspector);
    if (filterType) filtered = filtered.filter(o => o.flow_type === filterType);
    if (filterDate) filtered = filtered.filter(o => o.route_date === filterDate);
    if (filterInvestigation) filtered = filtered.filter(o => o.under_investigation);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(o => o.order_number?.toLowerCase().includes(q) || o.inspector?.toLowerCase().includes(q) || o.check_number?.toLowerCase().includes(q) || o.address?.toLowerCase().includes(q) || o.city?.toLowerCase().includes(q));
    }
    // Sort
    filtered = [...filtered].sort((a, b) => {
      let va = a[sortCol] ?? "", vb = b[sortCol] ?? "";
      if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return filtered;
  }, [data, search, cardFilter, filterInspector, filterType, filterDate, filterInvestigation, sortCol, sortDir]);

  const toggleCard = (key) => { setCardFilter(cardFilter === key ? null : key); setShowCheckDetail(false); setShowNonPayable(false); };
  const toggleSort = (col) => {
    const apiCol = col === "order_number" ? "order_number" : col === "inspector" ? "inspector" : col === "route_date" ? "route_date" : col === "flow_type" ? "flow_type" : col === "inspector_pay" ? "inspector_pay" : col === "expected" ? "expected" : col === "received" ? "received" : col === "check_number" ? "check_number" : col;
    if (sortCol === apiCol) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortCol(apiCol); setSortDir("asc"); }
  };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="h-3 w-3 text-gray-300 inline ml-0.5" />;
    return sortDir === "asc" ? <ChevronUp className="h-3 w-3 text-[#2F5D8A] inline ml-0.5" /> : <ChevronDown className="h-3 w-3 text-[#2F5D8A] inline ml-0.5" />;
  };
  const hasFilters = filterInspector || filterType || filterDate || filterInvestigation;

  return (
    <div className="space-y-4" data-testid="orders-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Orders by Closing Week</p>
          <p className="text-gray-400 text-xs mt-0.5">Source: Daily Close. Only payable orders in totals. Received from cheque import only.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchOrders} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg flex items-center gap-1.5" data-testid="orders-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={async () => {
              if (!selectedWeek) return;
              try {
                const res = await axios.get(
                  `${API}/finance/orders-by-week/export`,
                  { params: { week_start: selectedWeek.start, week_end: selectedWeek.end }, responseType: "blob" }
                );
                const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `finance-orders_${selectedWeek.start}_to_${selectedWeek.end}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                alert(err.response?.data?.detail || "Export failed");
              }
            }}
            disabled={!selectedWeek || loading}
            className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#264A6E] disabled:opacity-50 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
            data-testid="orders-export-excel-btn"
            title="Download .xlsx with all rows and the weekly summary"
          >
            <Download className="h-3.5 w-3.5" /> Export Excel
          </button>
        </div>
      </div>

      <WeekSelector weeks={weeks} selected={selectedWeek} onSelect={setSelectedWeek} color="#2F5D8A" />

      {loading ? <Spinner /> : !data ? (
        <div className="text-center py-10 text-gray-400 text-sm">Select a closing week.</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="orders-summary">
            <button onClick={() => toggleCard("all")} className={`bg-white rounded-lg border p-3 text-left cursor-pointer hover:shadow-md transition-all ${cardFilter === "all" ? "ring-2 ring-[#1F2937]" : "border-gray-200"}`} data-testid="card-total-orders">
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Total Payable (Unique)</p>
              <p className="text-2xl font-bold text-[#1F2937]">{s.total_payable || 0}</p>
              {s.duplicate_rows_removed > 0 && <p className="text-[9px] text-[#B08A3E]">{s.duplicate_rows_removed} duplicate rows excluded</p>}
            </button>
            <button onClick={() => toggleCard("expected")} className={`bg-white rounded-lg border p-3 text-left cursor-pointer hover:shadow-md transition-all ${cardFilter === "expected" ? "ring-2 ring-[#2F5D8A]" : "border-[#2F5D8A]/30"}`} data-testid="card-expected">
              <p className="text-[10px] uppercase text-[#2F5D8A] font-semibold">Expected Revenue</p>
              <p className="text-xl font-bold text-[#2F5D8A]">${(s.total_expected || 0).toFixed(2)}</p>
            </button>
            <button onClick={() => { toggleCard("received"); }} className={`bg-white rounded-lg border p-3 text-left cursor-pointer hover:shadow-md transition-all ${cardFilter === "received" ? "ring-2 ring-[#6B8F62]" : "border-[#6B8F62]/30"}`} data-testid="card-received">
              <p className="text-[10px] uppercase text-[#6B8F62] font-semibold">Received (Cheque)</p>
              <p className="text-xl font-bold text-[#6B8F62]">${(s.total_received || 0).toFixed(2)}</p>
              <p className="text-[9px] text-gray-300"><span className="text-[#2F5D8A] cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowCheckDetail(!showCheckDetail); setShowNonPayable(false); }}>Audit cheques</span></p>
            </button>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Inspector Cost</p>
              <p className="text-xl font-bold text-gray-600">${(s.total_inspector_pay || 0).toFixed(2)}</p>
              <p className="text-[9px] text-gray-300">From inspector profiles</p>
            </div>
            <div className={`bg-white rounded-lg border p-3 ${(s.net_margin || 0) >= 0 ? "border-[#6B8F62]/30" : "border-[#C07070]/30"}`}>
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Net Margin</p>
              <p className={`text-xl font-bold ${(s.net_margin || 0) >= 0 ? "text-[#6B8F62]" : "text-[#C07070]"}`}>${(s.net_margin || 0).toFixed(2)}</p>
              <p className="text-[9px] text-gray-300">Revenue - Inspector Cost</p>
            </div>
            <button onClick={() => toggleCard("missing")} className={`bg-white rounded-lg border p-3 text-left cursor-pointer hover:shadow-md transition-all ${cardFilter === "missing" ? "ring-2 ring-[#C07070]" : "border-[#C07070]/30"}`} data-testid="card-outstanding">
              <p className="text-[10px] uppercase text-[#C07070] font-semibold">Outstanding</p>
              <p className="text-xl font-bold text-[#C07070]">${(s.outstanding || 0).toFixed(2)}</p>
              <p className="text-[9px] text-gray-300">Unpaid only (Received = 0)</p>
            </button>
          </div>

          {/* Filter Row */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => toggleCard("missing")} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${cardFilter === "missing" ? "bg-[#C07070] text-white border-[#C07070]" : "bg-white text-[#C07070] border-[#C07070]/30 hover:border-[#C07070]"}`} data-testid="filter-missing">Missing: {s.missing_payments || 0}</button>
            <button onClick={() => toggleCard("underpaid")} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${cardFilter === "underpaid" ? "bg-[#B08A3E] text-white border-[#B08A3E]" : "bg-white text-[#B08A3E] border-[#B08A3E]/30 hover:border-[#B08A3E]"}`} data-testid="filter-underpaid">Underpaid: {s.diff_underpaid || 0}</button>
            <button onClick={() => toggleCard("overpaid")} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${cardFilter === "overpaid" ? "bg-[#6B5B8D] text-white border-[#6B5B8D]" : "bg-white text-[#6B5B8D] border-[#6B5B8D]/30 hover:border-[#6B5B8D]"}`} data-testid="filter-overpaid">Overpaid: {s.diff_overpaid || 0}</button>
            {(s.total_non_payable || 0) > 0 && (
              <button onClick={() => { setShowNonPayable(!showNonPayable); setShowCheckDetail(false); setCardFilter(null); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${showNonPayable ? "bg-gray-500 text-white border-gray-500" : "bg-white text-gray-500 border-gray-300 hover:border-gray-500"}`}
                data-testid="filter-non-payable"
              >Non-Payable: {s.total_non_payable}</button>
            )}
            {cardFilter && <button onClick={() => setCardFilter(null)} className="text-xs text-[#C07070] hover:underline ml-1">Clear</button>}
            {(s.investigation_count || 0) > 0 && (
              <button onClick={() => { setFilterInvestigation(!filterInvestigation); setCardFilter(null); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${filterInvestigation ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-white text-[#DC2626] border-[#DC2626]/30 hover:border-[#DC2626]"}`}
                data-testid="filter-investigation">
                Under Investigation: {s.investigation_count}
              </button>
            )}

            {/* Dropdown Filters */}
            <div className="flex items-center gap-1.5 ml-2 border-l border-gray-200 pl-2">
              <Filter className="h-3 w-3 text-gray-400" />
              <select value={filterInspector} onChange={e => setFilterInspector(e.target.value)} className="px-2 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="filter-inspector-dropdown">
                <option value="">All Inspectors</option>
                {inspectorList.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-2 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="filter-type-dropdown">
                <option value="">All Types</option>
                {typeList.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={filterDate} onChange={e => setFilterDate(e.target.value)} className="px-2 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="filter-date-dropdown">
                <option value="">All Dates</option>
                {dateList.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              {hasFilters && <button onClick={() => { setFilterInspector(""); setFilterType(""); setFilterDate(""); setFilterInvestigation(false); }} className="text-[10px] text-[#C07070] hover:underline">Clear</button>}
            </div>

            <div className="ml-auto flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order, inspector, cheque..."
                className="px-2.5 py-1 border border-gray-200 rounded text-xs w-48 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]" data-testid="orders-search" />
            </div>
            <span className="text-xs text-gray-400">{orders.length} orders</span>
          </div>

          {/* Check detail audit */}
          {showCheckDetail && data?.check_detail && (
            <div className="bg-white rounded-lg border border-[#6B8F62]/30 p-4 space-y-2" data-testid="check-detail-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[#6B8F62] uppercase">Cheque Import Audit — {data.check_detail.length} rows, ${(s.total_received || 0).toFixed(2)}</p>
                <button onClick={() => setShowCheckDetail(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
              </div>
              <div className="max-h-[220px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50"><tr className="border-b border-gray-200">
                    <TH>Order #</TH><TH>Check #</TH><TH align="right">Amount</TH><TH>Check Date</TH>
                  </tr></thead>
                  <tbody>{data.check_detail.slice(0, 500).map((r, i) => (
                    <tr key={i} className="border-b border-gray-100"><td className="px-3 py-1.5 text-gray-800">{r.order_number}</td><td className="px-3 py-1.5 text-[#2F5D8A]">{r.check_number}</td><td className="px-3 py-1.5 text-right font-bold text-[#6B8F62]">${(r.amount||0).toFixed(2)}</td><td className="px-3 py-1.5 text-gray-400">{r.check_date}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Non-payable list */}
          {showNonPayable && data?.non_payable && (
            <div className="bg-white rounded-lg border border-gray-300 p-4 space-y-2" data-testid="non-payable-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase">Non-Payable — {data.non_payable.length} orders excluded</p>
                <button onClick={() => setShowNonPayable(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
              </div>
              <div className="max-h-[220px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50"><tr className="border-b border-gray-200"><TH>Order #</TH><TH>Inspector</TH><TH>Date</TH><TH>Reason</TH></tr></thead>
                  <tbody>{data.non_payable.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100"><td className="px-3 py-1.5 text-gray-800">{r.order_number}</td><td className="px-3 py-1.5 text-gray-500">{r.inspector}</td><td className="px-3 py-1.5 text-gray-400">{r.route_date}</td><td className="px-3 py-1.5"><span className="px-1.5 py-0.5 bg-[#B08A3E]/10 text-[#B08A3E] rounded text-[10px] font-medium">{r.action}</span></td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Main table with sortable headers */}
          {!showNonPayable && !showCheckDetail && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="orders-table">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 z-10"><tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("order_number")}>Order # <SortIcon col="order_number" /></th>
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("address")}>Address <SortIcon col="address" /></th>
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("city")}>City <SortIcon col="city" /></th>
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("route_date")}>Route Date <SortIcon col="route_date" /></th>
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("inspector")}>Inspector <SortIcon col="inspector" /></th>
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("flow_type")}>Type <SortIcon col="flow_type" /></th>
                    <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("inspector_pay")}>Insp. Pay <SortIcon col="inspector_pay" /></th>
                    <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("expected")}>Expected <SortIcon col="expected" /></th>
                    <th className="px-2 py-2 text-right text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("received")}>Received <SortIcon col="received" /></th>
                    <th className="px-2 py-2 text-left text-[10px] uppercase text-gray-500 font-semibold cursor-pointer select-none hover:text-[#2F5D8A]" onClick={() => toggleSort("check_number")}>Cheque # <SortIcon col="check_number" /></th>
                  </tr></thead>
                  <tbody>{orders.length === 0 ? (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">No orders match</td></tr>
                  ) : orders.map((o, idx) => {
                    const diff = o.received > 0 && o.expected > 0 ? o.received - o.expected : null;
                    const isUnder = diff !== null && diff < -0.01;
                    const isOver = diff !== null && diff > 0.01;
                    const isMissing = o.expected > 0 && o.received === 0;
                    const isExpanded = expandedOn === o.order_number;
                    const isEditingThis = editingOn === o.order_number;
                    return (
                      <React.Fragment key={`${o.order_number}-${idx}`}>
                      <tr
                        onClick={() => toggleExpand(o.order_number)}
                        className={`border-b border-gray-100 cursor-pointer transition-colors ${isExpanded ? "bg-[#2F5D8A]/8 border-l-2 border-l-[#2F5D8A]" : "hover:bg-gray-50"} ${o.under_investigation ? "bg-[#DC2626]/8" : isMissing && !isExpanded ? "bg-[#C07070]/5" : isUnder && !isExpanded ? "bg-[#B08A3E]/5" : isOver && !isExpanded ? "bg-[#6B5B8D]/5" : ""}`}
                        data-testid={`order-row-${o.order_number}`}
                      >
                        <td className="px-2 py-2 font-medium text-gray-800">
                          <div className="flex items-center gap-1">
                            {isExpanded ? <ChevronUp className="h-3 w-3 text-[#2F5D8A] flex-shrink-0" /> : <ChevronDown className="h-3 w-3 text-gray-300 flex-shrink-0" />}
                            <span className="whitespace-nowrap">{o.order_number}</span>
                            {o.under_investigation && <span className="px-1 py-0.5 bg-[#DC2626] text-white text-[8px] font-bold rounded">NOT PAID</span>}
                            {o.has_adjustment && Math.abs((o.original_inspector_pay || 0) - (o.inspector_pay || 0)) > 0.01 && <span className="px-1 py-0.5 bg-[#B08A3E] text-white text-[8px] font-bold rounded" data-testid={`adj-badge-${o.order_number}`} title={o.adjustment?.reason || "Adjustment"}>ADJ</span>}
                            {o.has_adjustment && Math.abs((o.original_inspector_pay || 0) - (o.inspector_pay || 0)) > 0.01 && o.adjustment?.reason && <span className="text-[9px] text-[#B08A3E] italic font-medium truncate max-w-[160px]" data-testid={`adj-reason-${o.order_number}`} title={o.adjustment.reason}>{o.adjustment.reason}</span>}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-gray-500 text-[10px] max-w-[140px] truncate" title={o.address}>{o.address || "-"}</td>
                        <td className="px-2 py-2 text-gray-500 text-[10px]">{o.city || "-"}</td>
                        <td className="px-2 py-2 text-gray-400 text-[10px]">{o.route_date}</td>
                        <td className="px-2 py-2 text-gray-500 text-[10px] max-w-[120px] truncate" title={o.inspector}>{o.inspector}</td>
                        <td className="px-2 py-2 text-gray-400 text-[10px]">{o.flow_type}</td>
                        <td className={`px-2 py-1 text-right text-[10px] ${o.under_investigation ? "text-[#DC2626] font-bold" : o.has_adjustment ? "text-[#B08A3E] font-bold" : "text-gray-500"}`}>
                          {isEditingThis ? (
                            <div className="flex flex-col items-end gap-0.5" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] text-gray-400">$</span>
                                <input type="number" step="0.01" min="0" value={editPay} onChange={e => setEditPay(e.target.value)}
                                  className="w-16 px-1 py-0.5 border border-[#2F5D8A] rounded text-[10px] text-right focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                                  autoFocus data-testid="inline-edit-pay" />
                              </div>
                              <input type="text" value={editReason} onChange={e => setEditReason(e.target.value)}
                                placeholder="Reason..."
                                className="w-full px-1 py-0.5 border border-gray-200 rounded text-[9px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                                data-testid="inline-edit-reason" />
                              <div className="flex items-center gap-1">
                                <button onClick={(e) => saveInlineEdit(o, e)} disabled={editSaving}
                                  className="px-1.5 py-0.5 bg-[#2F5D8A] text-white rounded text-[9px] font-medium hover:bg-[#24496e] disabled:opacity-50"
                                  data-testid="inline-edit-save"><Save className="h-2.5 w-2.5 inline" /> {editSaving ? "..." : "OK"}</button>
                                <button onClick={cancelInlineEdit}
                                  className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-[9px] hover:bg-gray-300"
                                  data-testid="inline-edit-cancel"><X className="h-2.5 w-2.5 inline" /></button>
                              </div>
                              {editMsg && <span className={`text-[8px] ${editMsg === "Saved!" ? "text-[#6B8F62]" : "text-[#DC2626]"}`}>{editMsg}</span>}
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <div>
                                {o.under_investigation ? "$0.00" : o.inspector_pay > 0 ? `$${o.inspector_pay.toFixed(2)}` : "-"}
                                {o.has_adjustment && Math.abs((o.original_inspector_pay || 0) - (o.inspector_pay || 0)) > 0.01 && <span className="block text-[9px] text-gray-400 line-through">${o.original_inspector_pay?.toFixed(2)}</span>}
                                {o.has_adjustment && Math.abs((o.original_inspector_pay || 0) - (o.inspector_pay || 0)) > 0.01 && o.adjustment?.reason && <span className="block text-[9px] text-[#B08A3E] italic font-medium" title={o.adjustment.reason}>{o.adjustment.reason}</span>}
                              </div>
                              <button onClick={(e) => startInlineEdit(o, e)} title="Edit Inspector Pay"
                                className="p-0.5 rounded hover:bg-[#2F5D8A]/10 text-gray-300 hover:text-[#2F5D8A] transition-colors"
                                data-testid={`edit-pay-${o.order_number}`}>
                                <Edit2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right text-[10px] text-[#2F5D8A] font-medium">{o.expected > 0 ? `$${o.expected.toFixed(2)}` : "-"}</td>
                        <td className={`px-2 py-2 text-right text-[10px] font-bold ${isMissing ? "text-[#C07070]" : isUnder ? "text-[#B08A3E]" : isOver ? "text-[#6B5B8D]" : "text-[#6B8F62]"}`}>
                          {o.received > 0 ? `$${o.received.toFixed(2)}` : isMissing ? "MISSING" : "-"}
                        </td>
                        <td className="px-2 py-2 text-[10px] text-[#2F5D8A]">{o.check_number || "-"}</td>
                      </tr>
                      {/* ── EXPANDED ROW ── */}
                      {isExpanded && (
                        <tr data-testid={`order-expand-${o.order_number}`}>
                          <td colSpan={10} className="p-0 bg-[#f8fafb] border-b-2 border-[#2F5D8A]/20">
                            {expandLoading ? (
                              <div className="flex items-center justify-center py-6">
                                <div className="animate-spin h-4 w-4 border-2 border-[#2F5D8A] border-t-transparent rounded-full" />
                                <span className="ml-2 text-xs text-gray-400">Loading...</span>
                              </div>
                            ) : expandData?.found ? (
                              <div className="p-4 space-y-3">
                                {/* ── Row 1: Order Info + Payment ── */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {/* Order Info */}
                                  <div className="bg-white rounded-lg border border-gray-200 p-3" data-testid="expand-info-panel">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Order Info</p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                      <div><span className="text-gray-400">Address:</span> <span className="text-gray-700 font-medium">{expandData.info.address}, {expandData.info.city} {expandData.info.state} {expandData.info.zip}</span></div>
                                      <div><span className="text-gray-400">Status:</span> <span className="text-gray-700 font-medium">{expandData.info.status}</span></div>
                                      <div><span className="text-gray-400">Inspector:</span> <span className="text-gray-700 font-medium">{expandData.info.assigned_inspector}</span></div>
                                      <div><span className="text-gray-400">Type:</span> <span className="text-gray-700 font-medium">{expandData.info.flow_type} / {expandData.info.order_type}</span></div>
                                      <div><span className="text-gray-400">Company:</span> <span className="text-gray-700">{expandData.info.company}</span></div>
                                      <div><span className="text-gray-400">Queue:</span> <span className="text-gray-700">{expandData.info.queue}</span></div>
                                      <div><span className="text-gray-400">QA:</span> <span className="text-gray-700">{expandData.info.qa_status} {expandData.info.qa_result && `— ${expandData.info.qa_result}`}</span></div>
                                      <div><span className="text-gray-400">Transmitted:</span> <span className="text-gray-700">{expandData.info.transmitted || "no"}</span></div>
                                      {expandData.info.comments && <div className="col-span-2"><span className="text-gray-400">Comments:</span> <span className="text-gray-600 italic">{expandData.info.comments}</span></div>}
                                      {expandData.info.notes && <div className="col-span-2"><span className="text-gray-400">Notes:</span> <span className="text-gray-600 italic">{expandData.info.notes}</span></div>}
                                    </div>
                                  </div>

                                  {/* Payment + Adjustment */}
                                  <div className="bg-white rounded-lg border border-gray-200 p-3" data-testid="expand-payment-panel">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Payment</p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
                                      <div><span className="text-gray-400">Profile Rate:</span> <span className="text-gray-700 font-bold">${expandData.payment.profile_rate?.toFixed(2)}</span></div>
                                      <div><span className="text-gray-400">Expected (Safeguard):</span> <span className="text-[#2F5D8A] font-bold">${expandData.payment.expected?.toFixed(2)}</span></div>
                                      <div><span className="text-gray-400">Received (Cheques):</span> <span className="text-[#6B8F62] font-bold">${expandData.payment.total_received?.toFixed(2)}</span></div>
                                      <div><span className="text-gray-400">Closing Action:</span> <span className="text-gray-700">{expandData.closing.action}</span></div>
                                      {expandData.payment.checks?.length > 0 && (
                                        <div className="col-span-2">
                                          <span className="text-gray-400">Cheques:</span>
                                          {expandData.payment.checks.map((ck, ci) => (
                                            <span key={ci} className="ml-1 px-1.5 py-0.5 bg-[#2F5D8A]/10 text-[#2F5D8A] rounded text-[10px]">#{ck.check_number} ${ck.amount.toFixed(2)}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    {/* Current adjustment display */}
                                    {expandData.adjustment && (
                                      <div className="bg-[#B08A3E]/8 border border-[#B08A3E]/20 rounded p-2 mb-2">
                                        <p className="text-[10px] font-bold text-[#B08A3E] uppercase">Current Adjustment</p>
                                        <p className="text-xs text-gray-700">
                                          <span className="line-through text-gray-400">${expandData.adjustment.original_pay?.toFixed(2)}</span>
                                          {" → "}
                                          <span className="font-bold text-[#B08A3E]">${expandData.adjustment.adjusted_pay?.toFixed(2)}</span>
                                        </p>
                                        <p className="text-[10px] text-gray-500 italic">"{expandData.adjustment.reason}"</p>
                                        <p className="text-[9px] text-gray-400">by {expandData.adjustment.adjusted_by} at {new Date(expandData.adjustment.adjusted_at).toLocaleString()}</p>
                                      </div>
                                    )}
                                    {/* Adjustment form */}
                                    {expandData.can_edit && (
                                      <div className="border-t border-gray-100 pt-2">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">{expandData.adjustment ? "Update" : "Adjust"} Inspector Pay</p>
                                        <div className="flex items-end gap-2">
                                          <div>
                                            <label className="text-[9px] text-gray-400">Adjusted Pay ($)</label>
                                            <input type="number" step="0.01" min="0" value={adjPay} onChange={e => setAdjPay(e.target.value)}
                                              className="block w-24 px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                                              placeholder="0.00" data-testid="adj-pay-input" />
                                          </div>
                                          <div className="flex-1">
                                            <label className="text-[9px] text-gray-400">Reason (required)</label>
                                            <input type="text" value={adjReason} onChange={e => setAdjReason(e.target.value)}
                                              className="block w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                                              placeholder="e.g. Internal changed to External" data-testid="adj-reason-input" />
                                          </div>
                                          <button onClick={(e) => { e.stopPropagation(); saveAdjustment(o); }}
                                            disabled={adjSaving}
                                            className="px-3 py-1 bg-[#2F5D8A] text-white rounded text-xs font-medium hover:bg-[#24496e] disabled:opacity-50"
                                            data-testid="adj-save-btn">
                                            {adjSaving ? "..." : "Save"}
                                          </button>
                                        </div>
                                        {adjMsg && <p className={`text-[10px] mt-1 ${adjMsg === "Saved!" ? "text-[#6B8F62]" : "text-[#DC2626]"}`}>{adjMsg}</p>}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* ── Row 2: Activity History ── */}
                                <div className="bg-white rounded-lg border border-gray-200 p-3" data-testid="expand-history-panel">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    Activity History ({expandData.event_history?.length || 0} events)
                                  </p>
                                  {expandData.event_history?.length > 0 ? (
                                    <div className="max-h-[180px] overflow-y-auto space-y-1">
                                      {expandData.event_history.map((ev, ei) => (
                                        <div key={ei} className="flex items-start gap-2 text-[10px] py-1 border-b border-gray-50">
                                          <span className="text-gray-300 whitespace-nowrap min-w-[120px]">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ""}</span>
                                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[9px] font-medium whitespace-nowrap">{ev.event?.replace(/_/g, " ")}</span>
                                          <span className="text-gray-600 flex-1">{ev.detail}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-300">No activity history for this order.</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="py-4 text-center text-xs text-gray-400">Order not found in system.</div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}</tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SendFollowUpModule = () => {
  const [searchText, setSearchText] = useState("");
  const [orders, setOrders] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reason, setReason] = useState("");
  const [images, setImages] = useState([]); // [{url, filename}]
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [sentHistory, setSentHistory] = useState([]);
  const [errorSource, setErrorSource] = useState("inspector");
  const [removePayment, setRemovePayment] = useState(true);
  const fileRef = useRef(null);

  // Load sent follow-ups history
  useEffect(() => {
    axios.get(`${API}/finance/followup-history`).then(res => setSentHistory(res.data || [])).catch(() => {});
  }, []);

  const searchOrders = async () => {
    if (!searchText.trim()) return;
    setLoadingSearch(true);
    try {
      const res = await axios.get(`${API}/finance/orders-unified?search=${encodeURIComponent(searchText)}&limit=20`);
      setOrders(res.data.orders || []);
    } catch {} finally { setLoadingSearch(false); }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      try {
        const res = await axios.post(`${API}/finance/upload-followup-image`, fd);
        setImages(prev => [...prev, { url: res.data.url, filename: res.data.filename }]);
      } catch {}
    }
    if (fileRef.current) fileRef.current.value = "";
    setUploading(false);
  };

  const sendFollowUp = async () => {
    if (!selectedOrder) { setMessage({ type: "error", text: "Select an order first" }); return; }
    if (images.length < 2) { setMessage({ type: "error", text: "Both images required: issue photo + Safeguard email proof" }); return; }
    if (!reason.trim()) { setMessage({ type: "error", text: "Enter a reason for the follow-up" }); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/finance/send-followup`, {
        order_id: selectedOrder.order_id,
        reason: reason.trim(),
        error_source: errorSource,
        remove_payment: removePayment,
        proof_images: images.map(img => img.url),
      });
      setMessage({ type: "success", text: `Follow-up sent to ${res.data.inspector} for ${res.data.order_number}. ${res.data.payment_removed ? "Payment removed from " + (res.data.original_close_date || "original close day") + "." : "Payment kept."}` });
      setSelectedOrder(null); setReason(""); setImages([]); setOrders([]);
      setErrorSource("inspector"); setRemovePayment(true);
      // Refresh history
      axios.get(`${API}/finance/followup-history`).then(r => setSentHistory(r.data || [])).catch(() => {});
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to send follow-up" });
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="send-followup-module">
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Send Follow-Up to Inspector</p>
        <p className="text-gray-400 text-[10px]">Send urgent manual follow-up alerts with proof images</p>
      </div>

      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}

      {/* Step 1: Search & Select Order */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <p className="text-sm font-bold text-gray-800">1. Select Order</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-gray-400" />
            <input value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === "Enter" && searchOrders()} placeholder="Search by order number, company, inspector..." className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="followup-search" />
          </div>
          <button onClick={searchOrders} className="px-4 py-2 bg-[#2F5D8A] text-white text-sm rounded-lg" data-testid="followup-search-btn">Search</button>
        </div>
        {loadingSearch && <Spinner />}
        {orders.length > 0 && !selectedOrder && (
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {orders.map(o => (
              <div key={o.order_id} onClick={() => setSelectedOrder(o)} className="px-3 py-2 hover:bg-[#2F5D8A]/5 cursor-pointer border-b border-gray-100 last:border-0" data-testid={`followup-order-${o.order_number}`}>
                <span className="text-sm font-medium text-[#2F5D8A]">{o.order_number}</span>
                <span className="text-xs text-gray-500 ml-2">{o.company} · {o.inspector}</span>
                <span className="text-xs text-gray-400 ml-2">{o.address}</span>
              </div>
            ))}
          </div>
        )}
        {selectedOrder && (
          <div className="bg-[#2F5D8A]/5 border border-[#2F5D8A]/20 rounded-lg p-3 flex items-center justify-between" data-testid="followup-selected-order">
            <div>
              <p className="text-sm font-bold text-[#2F5D8A]">{selectedOrder.order_number}</p>
              <p className="text-xs text-gray-500">{selectedOrder.company} · {selectedOrder.inspector} · {selectedOrder.address}</p>
            </div>
            <button onClick={() => { setSelectedOrder(null); setOrders([]); }} className="text-xs text-red-500 hover:underline">Change</button>
          </div>
        )}
      </div>

      {/* Step 2: Upload Proof Images */}
      {selectedOrder && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <p className="text-sm font-bold text-gray-800">2. Upload Proof Images <span className="text-red-500">(both required)</span></p>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="px-4 py-2 bg-[#B08A3E] hover:bg-[#9A7A3E] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="followup-upload-btn">
              <Image className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : "Upload Images"}
            </button>
            <span className="text-[10px] text-gray-400">{images.length}/2 — Issue photo + Safeguard email screenshot</span>
          </div>
          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {images.map((img, idx) => (
                <div key={idx} className="relative rounded-lg overflow-hidden border border-gray-200 group">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-full h-32 object-cover" />
                  <button onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))} className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
                  <p className="text-[9px] text-gray-500 text-center py-0.5 bg-gray-50">{idx === 0 ? "Issue Photo" : "Safeguard Email"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Reason + Send */}
      {selectedOrder && images.length >= 2 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <p className="text-sm font-bold text-gray-800">3. Reason, Blame & Send</p>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Describe the issue / reason for follow-up..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="followup-reason" />
          <div className="flex items-center gap-3 flex-wrap bg-gray-50 p-2.5 rounded-lg border border-gray-200">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Error source:</span>
            {[
              { key: "inspector", label: "Inspector Error", removePay: true, color: "#C07070" },
              { key: "client", label: "Client Error", removePay: false, color: "#2F5D8A" },
              { key: "safeguard", label: "Safeguard Error", removePay: false, color: "#B08A3E" },
            ].map(src => (
              <label key={src.key} className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold cursor-pointer border transition-all ${errorSource === src.key ? "text-white" : "bg-white border-gray-300 text-gray-600"}`}
                style={errorSource === src.key ? { backgroundColor: src.color, borderColor: src.color } : {}}
                data-testid={`error-source-${src.key}`}>
                <input type="radio" name="error_source" value={src.key} checked={errorSource === src.key} onChange={e => { setErrorSource(e.target.value); setRemovePayment(src.removePay); }} className="hidden" />
                {src.label}
              </label>
            ))}
            <label className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-600">
              <input type="checkbox" checked={removePayment} onChange={e => setRemovePayment(e.target.checked)} className="accent-[#C07070]" data-testid="followup-remove-pay" />
              Remove inspector payment
            </label>
          </div>
          <button onClick={sendFollowUp} disabled={sending} className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" data-testid="followup-send-btn">
            <AlertCircle className="h-4 w-4" /> {sending ? "Sending..." : "SEND FOLLOW-UP TO INSPECTOR"}
          </button>
        </div>
      )}

      {/* Follow-Up Queue (Full Workflow) */}
      {sentHistory.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="followup-queue">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <p className="text-sm font-bold text-gray-800">Follow-Up Queue</p>
            <span className="text-[10px] text-gray-500">{sentHistory.filter(h => h.status !== "completed").length} open · {sentHistory.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="followup-queue-table">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-2 text-left">Order #</th>
                  <th className="px-2 py-2 text-left">Inspector</th>
                  <th className="px-2 py-2 text-left">Original Close</th>
                  <th className="px-2 py-2 text-left">Reason</th>
                  <th className="px-2 py-2 text-left">Sent</th>
                  <th className="px-2 py-2 text-left">Ack</th>
                  <th className="px-2 py-2 text-left">Assigned To</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sentHistory.map(h => {
                  const statusPalette = {
                    open: { bg: "#C07070", label: "OPEN" },
                    acknowledged: { bg: "#2F5D8A", label: "ACKNOWLEDGED" },
                    routed: { bg: "#B08A3E", label: "ROUTED" },
                    completed: { bg: "#6B8F62", label: "COMPLETED" },
                  };
                  const p = statusPalette[h.status] || { bg: "#6B7280", label: (h.status || "").toUpperCase() };
                  return (
                    <tr key={h.id} className="border-t border-gray-100 hover:bg-gray-50 align-top" data-testid={`followup-row-${h.order_number}`}>
                      <td className="px-2 py-2 font-semibold text-gray-800">{h.order_number}</td>
                      <td className="px-2 py-2 text-gray-600">{h.inspector}</td>
                      <td className="px-2 py-2 text-gray-500">{h.original_close_date || "-"}</td>
                      <td className="px-2 py-2 text-gray-500 max-w-[180px] truncate" title={h.reason}>
                        {h.reason?.slice(0, 40)}
                        <span className="ml-1 text-[9px] font-bold text-gray-400">
                          [{h.error_source?.toUpperCase() || "INSP"}{h.payment_removed ? " · $✗" : ""}]
                        </span>
                      </td>
                      <td className="px-2 py-2 text-gray-500">{h.sent_at?.slice(0, 10)}</td>
                      <td className="px-2 py-2">
                        {h.acknowledged_at ? (
                          <span className="inline-flex items-center gap-0.5 text-[#6B8F62] font-bold text-[10px]" title={`${h.acknowledged_at?.slice(0,16)} by ${h.acknowledged_by || ''}`}>
                            <CheckCircle className="h-3 w-3" /> {h.acknowledged_at.slice(5, 16).replace("T", " ")}
                          </span>
                        ) : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-2 py-2">
                        <select
                          defaultValue={h.assigned_to || h.inspector}
                          onChange={async (e) => {
                            try {
                              await axios.patch(`${API}/finance/followups/${h.id}/reassign`, { assigned_to: e.target.value, action_type: "reassign" });
                              const r = await axios.get(`${API}/finance/followup-history`);
                              setSentHistory(r.data || []);
                            } catch {}
                          }}
                          className="text-[10px] bg-transparent border-0 text-gray-700 font-medium cursor-pointer"
                          data-testid={`followup-reassign-${h.order_number}`}>
                          <option value={h.inspector}>{h.inspector} (original)</option>
                          {(h.assigned_to && h.assigned_to !== h.inspector) && <option value={h.assigned_to}>{h.assigned_to}</option>}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ backgroundColor: p.bg }}>{p.label}</span>
                          {h.workflow_status && (
                            <span className="text-[9px] text-gray-600 font-semibold" data-testid={`followup-workflow-${h.order_number}`}>
                              {h.workflow_status}
                            </span>
                          )}
                          {h.payment_removed && (
                            <span className="text-[9px] text-[#8B4545] font-bold">Payment Removed</span>
                          )}
                          {h.inspector_response_option && (
                            <span className="text-[9px] text-gray-500 italic" title={h.inspector_comment}>
                              "{(h.inspector_comment || "").slice(0, 40)}{(h.inspector_comment || "").length > 40 ? "..." : ""}"
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-1">
                          {h.status === "acknowledged" && h.workflow_status !== "Follow-Up Reopened" && (
                            <button
                              onClick={async () => {
                                const note = window.prompt(
                                  `Reopen order ${h.order_number} into Follow-Up queue?\nPayment will be removed and order becomes assignable again.\n\nAdmin note (optional):`,
                                  ""
                                );
                                if (note === null) return;
                                try {
                                  await axios.post(`${API}/finance/followups/${h.id}/reopen-to-queue`, { admin_note: note });
                                  const r = await axios.get(`${API}/finance/followup-history`);
                                  setSentHistory(r.data || []);
                                } catch (e) {
                                  alert(e.response?.data?.detail || "Failed to reopen");
                                }
                              }}
                              className="px-2 py-0.5 bg-[#B08A3E] hover:bg-[#8F6F2F] text-white text-[10px] font-bold rounded"
                              data-testid={`followup-reopen-${h.order_number}`}>
                              Reopen
                            </button>
                          )}
                          {h.status !== "completed" && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Close follow-up for ${h.order_number}? This will restore the order.`)) return;
                                try {
                                  await axios.patch(`${API}/finance/followups/${h.id}/resolve`);
                                  const r = await axios.get(`${API}/finance/followup-history`);
                                  setSentHistory(r.data || []);
                                } catch {}
                              }}
                              className="px-2 py-0.5 bg-[#6B8F62] hover:bg-[#5A7D52] text-white text-[10px] font-bold rounded"
                              data-testid={`followup-resolve-${h.order_number}`}>
                              Resolve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


/* ════════════════════════════════════════════════════════
   SAFEGUARD RATES MODULE
   ════════════════════════════════════════════════════════ */
const SafeguardRatesModule = () => {
  const [data, setData] = useState({ base_rates: [], special_rates: [], order_overrides: [], payment_rates: [] });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    try { const res = await axios.get(`${API}/finance/company-rates`); setData(res.data); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const seedRates = async () => {
    try {
      const res = await axios.post(`${API}/finance/payment-rates/seed`);
      setMessage({ type: "success", text: `Seeded ${res.data.seeded} payment codes` }); fetchAll();
    } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Seed failed" }); }
  };

  return (
    <div className="space-y-4" data-testid="safeguard-rates-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Safeguard Payment Rates (Code-Based)</p>
          <p className="text-gray-400 text-xs mt-0.5">Reference rates by Payment Code (ZIP prefix)</p>
        </div>
        {((data.payment_rates || []).filter(r => r.company !== "National").length) === 0 && (
          <button onClick={seedRates} className="px-3 py-1.5 bg-[#6B8F62] hover:bg-[#5A7D52] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="seed-rates-btn">
            <Plus className="h-3.5 w-3.5" /> Seed Default Rates
          </button>
        )}
      </div>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}
      {loading ? <Spinner /> : <PaymentRatesSection rates={data.payment_rates || []} onRefresh={fetchAll} setMsg={setMessage} />}
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   NATIONAL MODULE (Manual Revenue)
   ════════════════════════════════════════════════════════ */
const NationalModule = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ order_id: "", inspector: "", revenue: "", notes: "" });
  const [message, setMessage] = useState(null);
  const [nationalRate, setNationalRate] = useState(null);
  const [editingRate, setEditingRate] = useState(false);
  const [rateForm, setRateForm] = useState({ regular: "", internal: "", external: "" });
  const [payments, setPayments] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);

  const fetchEntries = useCallback(async () => {
    try { const res = await axios.get(`${API}/finance/national-entries`); setEntries(res.data); } catch {} finally { setLoading(false); }
  }, []);
  const fetchNationalRate = useCallback(async () => {
    try { const res = await axios.get(`${API}/finance/payment-rates`); const nr = (res.data || []).find(r => r.code === "NATIONAL"); if (nr) { setNationalRate(nr); setRateForm({ regular: String(nr.regular), internal: String(nr.internal), external: String(nr.external) }); } } catch {}
  }, []);
  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try { const res = await axios.get(`${API}/finance/national-payments?weeks=6`); setPayments(res.data); } catch {} finally { setPaymentsLoading(false); }
  }, []);
  useEffect(() => { fetchEntries(); fetchNationalRate(); fetchPayments(); }, [fetchEntries, fetchNationalRate, fetchPayments]);

  const totalRevenue = useMemo(() => entries.reduce((s, e) => s + (parseFloat(e.revenue) || 0), 0), [entries]);

  const handleSave = async () => {
    if (!form.order_id || !form.revenue) { setMessage({ type: "error", text: "Order ID and revenue required" }); return; }
    try {
      if (editing) { await axios.put(`${API}/finance/national-entries/${editing}`, { ...form, revenue: parseFloat(form.revenue) }); setMessage({ type: "success", text: "Entry updated" }); }
      else { await axios.post(`${API}/finance/national-entries`, { ...form, revenue: parseFloat(form.revenue) }); setMessage({ type: "success", text: "Entry created" }); }
      setShowForm(false); setEditing(null); setForm({ order_id: "", inspector: "", revenue: "", notes: "" }); fetchEntries();
    } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };
  const handleEdit = (e) => { setForm({ order_id: e.order_id, inspector: e.inspector || "", revenue: String(e.revenue), notes: e.notes || "" }); setEditing(e.id); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/finance/national-entries/${id}`); fetchEntries(); setMessage({ type: "success", text: "Deleted" }); } catch { setMessage({ type: "error", text: "Delete failed" }); } };
  const saveRate = async () => {
    try { await axios.put(`${API}/finance/payment-rates/${nationalRate.id}`, { regular: parseFloat(rateForm.regular), internal: parseFloat(rateForm.internal), external: parseFloat(rateForm.external) }); setMessage({ type: "success", text: "National rate updated" }); setEditingRate(false); fetchNationalRate(); }
    catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };

  return (
    <div className="space-y-4" data-testid="national-module">
      {nationalRate && (
        <div className="bg-white rounded-lg border border-[#6B5B8D]/25 p-4" data-testid="national-rate-card">
          <div className="flex items-center justify-between mb-2">
            <div><p className="text-xs font-semibold text-[#6B5B8D] uppercase tracking-wider">National Default Rate</p><p className="text-gray-400 text-[10px]">Applied to all National orders automatically</p></div>
            {!editingRate && <button onClick={() => setEditingRate(true)} className="p-1.5 text-gray-400 hover:text-[#6B5B8D]" data-testid="edit-national-rate-btn"><Edit2 className="h-4 w-4" /></button>}
          </div>
          {editingRate ? (
            <div className="flex items-center gap-3">
              <div><label className="text-[9px] text-gray-400 uppercase">Regular</label><input value={rateForm.regular} onChange={e => setRateForm(p => ({...p, regular: e.target.value}))} className="w-20 px-2 py-1 border rounded text-sm text-right" type="number" step="0.01" data-testid="national-rate-regular" /></div>
              <div><label className="text-[9px] text-gray-400 uppercase">Internal</label><input value={rateForm.internal} onChange={e => setRateForm(p => ({...p, internal: e.target.value}))} className="w-20 px-2 py-1 border rounded text-sm text-right" type="number" step="0.01" data-testid="national-rate-internal" /></div>
              <div><label className="text-[9px] text-gray-400 uppercase">External</label><input value={rateForm.external} onChange={e => setRateForm(p => ({...p, external: e.target.value}))} className="w-20 px-2 py-1 border rounded text-sm text-right" type="number" step="0.01" data-testid="national-rate-external" /></div>
              <button onClick={saveRate} className="px-3 py-1.5 bg-[#6B8F62] text-white text-xs font-semibold rounded" data-testid="save-national-rate-btn"><Save className="h-3.5 w-3.5" /></button>
              <button onClick={() => setEditingRate(false)} className="text-gray-400 text-xs">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-6"><span className="text-2xl font-bold text-[#6B5B8D]">${parseFloat(nationalRate.regular).toFixed(2)}</span><span className="text-xs text-gray-400">per order (all types)</span></div>
          )}
        </div>
      )}

      {/* National Weekly Payments (auto-earned on completion) */}
      <div className="bg-white rounded-lg border border-[#6B5B8D]/25 overflow-hidden" data-testid="national-weekly-payments">
        <div className="px-4 py-3 bg-[#6B5B8D]/5 border-b border-[#6B5B8D]/20 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-[#6B5B8D] uppercase tracking-wider">National Weekly Payments</p>
            <p className="text-[10px] text-gray-500 mt-0.5">Completed National orders are paid to the inspector immediately - no Safeguard reconciliation required. Reschedule = $0.</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-gray-400 uppercase font-semibold">Grand Total</p>
            <p className="text-lg font-extrabold text-[#6B5B8D]" data-testid="national-grand-total">
              ${(payments?.grand_total_pay || 0).toFixed(2)}
              <span className="text-[10px] text-gray-400 ml-1.5 font-normal">({payments?.grand_total_orders || 0} orders)</span>
            </p>
          </div>
        </div>
        {paymentsLoading ? (
          <div className="py-8"><Spinner /></div>
        ) : !payments?.weeks?.length ? (
          <div className="text-center py-10 text-gray-400 text-sm">No National payments yet for periods from Apr 7, 2026 onwards.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {payments.weeks.map(w => {
              const isOpen = expandedWeek === w.period_start;
              return (
                <div key={w.period_start} data-testid={`national-week-${w.period_start}`}>
                  <button
                    onClick={() => setExpandedWeek(isOpen ? null : w.period_start)}
                    className="w-full px-4 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
                    data-testid={`national-week-toggle-${w.period_start}`}
                  >
                    {isOpen
                      ? <ChevronDown className="h-4 w-4 text-[#6B5B8D]" />
                      : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#1F2937]">
                        {new Date(w.period_start + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        {" - "}
                        {new Date(w.period_end + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                      <p className="text-[10px] text-gray-400">Pay date: {w.payment_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-gray-400 uppercase">Orders</p>
                      <p className="text-sm font-bold text-[#1F2937]">{w.total_orders}</p>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-[9px] text-gray-400 uppercase">Total Pay</p>
                      <p className="text-sm font-bold text-[#6B5B8D]">${w.total_pay.toFixed(2)}</p>
                    </div>
                    {w.reschedule_count > 0 && (
                      <div className="text-right min-w-[80px]">
                        <p className="text-[9px] text-gray-400 uppercase">Reschedule</p>
                        <p className="text-[11px] font-semibold text-gray-400" title="Reschedule orders = $0 pay">{w.reschedule_count} &times; $0</p>
                      </div>
                    )}
                  </button>
                  {isOpen && (
                    <div className="bg-gray-50/60 px-4 py-3">
                      {w.inspectors.length === 0 ? (
                        <p className="text-center text-gray-400 text-xs py-3">No completed National orders for this week.</p>
                      ) : (
                        <table className="w-full text-xs" data-testid={`national-week-insp-table-${w.period_start}`}>
                          <thead>
                            <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                              <th className="px-3 py-2 text-left">Inspector</th>
                              <th className="px-3 py-2 text-left">Orders</th>
                              <th className="px-3 py-2 text-left">Details</th>
                              <th className="px-3 py-2 text-right">Inspector Pay</th>
                            </tr>
                          </thead>
                          <tbody>
                            {w.inspectors.map(insp => (
                              <tr key={insp.name} className="border-b border-gray-100 align-top" data-testid={`national-insp-${w.period_start}-${insp.name}`}>
                                <td className="px-3 py-2 font-semibold text-[#1F2937]">{insp.name}</td>
                                <td className="px-3 py-2 text-gray-600">{insp.orders}</td>
                                <td className="px-3 py-2 text-[10px] text-gray-500">
                                  {insp.items.map((it, idx) => (
                                    <div key={idx} className="flex items-center gap-1.5">
                                      <span className="font-mono text-gray-700">{it.order_number}</span>
                                      <span className="text-gray-300">-</span>
                                      <span>{it.route_date}</span>
                                      <span className="text-gray-300">-</span>
                                      <span className="uppercase text-[9px] font-semibold text-[#6B5B8D]">{it.flow}</span>
                                      {it.adjusted && <span className="text-[9px] px-1 bg-amber-100 text-amber-700 rounded">ADJ</span>}
                                      <span className="ml-auto text-[#6B5B8D] font-bold">${it.amount.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </td>
                                <td className="px-3 py-2 text-right font-extrabold text-[#6B5B8D]">${insp.pay.toFixed(2)}</td>
                              </tr>
                            ))}
                            <tr className="bg-[#6B5B8D]/10 font-bold">
                              <td className="px-3 py-2 uppercase text-[10px] text-[#6B5B8D]">Week Total</td>
                              <td className="px-3 py-2 text-[#6B5B8D]">{w.total_orders}</td>
                              <td></td>
                              <td className="px-3 py-2 text-right text-[#6B5B8D]" data-testid={`national-week-total-${w.period_start}`}>${w.total_pay.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div><p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">National Orders (Manual Revenue)</p><p className="text-gray-400 text-xs mt-0.5">Per-order entries. Total: <span className="text-[#6B8F62] font-bold">${totalRevenue.toFixed(2)}</span></p></div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ order_id: "", inspector: "", revenue: "", notes: "" }); }} className="px-3 py-1.5 bg-[#B08A3E] hover:bg-[#9A7A3E] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="add-national-btn"><Plus className="h-3.5 w-3.5" /> Add Entry</button>
      </div>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}
      {showForm && (
        <div className="bg-white border border-[#B08A3E]/30 rounded-lg p-4 space-y-3" data-testid="national-form">
          <p className="text-sm font-semibold text-gray-800">{editing ? "Edit National Entry" : "New National Entry"}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FormInput label="Order ID" value={form.order_id} onChange={v => setForm(p => ({ ...p, order_id: v }))} placeholder="Order number" testId="national-order-id" />
            <FormInput label="Inspector" value={form.inspector} onChange={v => setForm(p => ({ ...p, inspector: v }))} placeholder="Inspector name" testId="national-inspector" />
            <FormInput label="Revenue ($)" value={form.revenue} onChange={v => setForm(p => ({ ...p, revenue: v }))} placeholder="0.00" type="number" testId="national-revenue" />
            <FormInput label="Notes" value={form.notes} onChange={v => setForm(p => ({ ...p, notes: v }))} placeholder="Notes..." testId="national-notes" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={handleSave} className="px-4 py-2 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="national-save-btn"><Save className="h-3.5 w-3.5" />{editing ? "Update" : "Save"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 text-gray-500 hover:text-gray-800 text-xs">Cancel</button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? <Spinner /> : entries.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No national entries. Click "Add Entry" to create one.</div>
        ) : (
          <table className="w-full text-sm" data-testid="national-table">
            <thead><tr className="border-b border-gray-200"><TH>Order ID</TH><TH>Inspector</TH><TH align="right">Revenue</TH><TH>Notes</TH><TH>Created By</TH><TH align="right">Actions</TH></tr></thead>
            <tbody>{entries.map(e => (
              <tr key={e.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`national-row-${e.id}`}>
                <td className="px-4 py-2.5 text-gray-800 font-medium">{e.order_id}</td>
                <td className="px-4 py-2.5 text-gray-600">{e.inspector || <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-2.5 text-right text-[#6B8F62] font-bold">${parseFloat(e.revenue).toFixed(2)}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{e.notes || <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-2.5 text-gray-400 text-xs">{e.created_by || "-"}</td>
                <td className="px-4 py-2.5 text-right"><ActionBtns onEdit={() => handleEdit(e)} onDelete={() => handleDelete(e.id)} id={e.id} prefix="national" /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   TRANSMISSION PAYMENT MODULE — WEEK CARDS → OPERATOR → DAY
   ════════════════════════════════════════════════════════ */
const TransmissionPaymentModule = () => {
  const weeks = useMemo(() => generateWeekPeriods(4), []);
  const [expandedWeek, setExpandedWeek] = useState(-1);
  const [weekData, setWeekData] = useState({});
  const [loadingWeek, setLoadingWeek] = useState(-1);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [message, setMessage] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [overridesByWeek, setOverridesByWeek] = useState({}); // week_start -> {inspector_overrides, extra_items}
  const [savingOverrides, setSavingOverrides] = useState(false);

  const loadWeek = useCallback(async (idx) => {
    setLoadingWeek(idx);
    try {
      const ws = weeks[idx].start;
      const [res, ovRes] = await Promise.all([
        axios.get(`${API}/finance/transmission-payment?week_start=${ws}`),
        axios.get(`${API}/finance/transmission-report-overrides?week_start=${ws}`).catch(() => ({ data: { inspector_overrides: {}, extra_items: [] } })),
      ]);
      setWeekData(prev => ({ ...prev, [idx]: res.data }));
      setOverridesByWeek(prev => ({
        ...prev,
        [ws]: {
          inspector_overrides: ovRes.data?.inspector_overrides || {},
          extra_items: ovRes.data?.extra_items || [],
        },
      }));
    } catch {} finally { setLoadingWeek(-1); }
  }, [weeks]);

  const toggleWeek = (idx) => {
    if (expandedWeek === idx) { setExpandedWeek(-1); return; }
    setExpandedWeek(idx); setSelectedOperator(null);
    if (!weekData[idx]) loadWeek(idx);
  };

  const confirmPayment = async (contractor) => {
    const data = weekData[expandedWeek];
    if (!data) return;
    setConfirming(true);
    try { await axios.post(`${API}/finance/transmission-payment/confirm`, { contractor, week_start: data.week_start }); setMessage({ type: "success", text: `Service payment confirmed for ${contractor}` }); loadWeek(expandedWeek); }
    catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
    finally { setConfirming(false); }
  };

  const discFileRef = useRef(null);
  const [generatingDisc, setGeneratingDisc] = useState(false);
  const handleDiscrepancyFile = async (e, contractor, weekStart, weekEnd) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGeneratingDisc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("week_start", weekStart);
      fd.append("week_end", weekEnd || "");
      fd.append("contractor", contractor);
      const res = await axios.post(`${API}/finance/transmission-payment/discrepancy-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${contractor.toLowerCase()}_discrepancy_${weekStart}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: "success", text: "Discrepancy PDF generated and downloaded" });
    } catch (err) {
      let msg = "Failed to generate PDF";
      if (err.response?.data) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch { msg = err.response?.data?.detail || msg; }
      }
      setMessage({ type: "error", text: msg });
    } finally {
      setGeneratingDisc(false);
      if (discFileRef.current) discFileRef.current.value = "";
    }
  };

  const getOverrides = (ws) => overridesByWeek[ws] || { inspector_overrides: {}, extra_items: [] };

  const setOverrides = (ws, patch) => {
    setOverridesByWeek(prev => ({
      ...prev,
      [ws]: { ...(prev[ws] || { inspector_overrides: {}, extra_items: [] }), ...patch },
    }));
  };

  const saveOverrides = async (ws) => {
    setSavingOverrides(true);
    try {
      const ov = getOverrides(ws);
      await axios.post(`${API}/finance/transmission-report-overrides`, {
        week_start: ws,
        inspector_overrides: ov.inspector_overrides,
        extra_items: ov.extra_items,
      });
      setMessage({ type: "success", text: "Adjustments saved" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save" });
    } finally { setSavingOverrides(false); }
  };

  // Compute effective rows (apply overrides)
  const computeEffectiveByInspector = (op, ov) => {
    const overrides = ov?.inspector_overrides || {};
    const rows = op.by_inspector.map(i => {
      const o = overrides[i.name];
      const hasOverride = o !== undefined && Number.isFinite(o.amount);
      const paidDirectly = !!o?.paid_directly_by_inspector;
      const effectiveAmount = hasOverride ? Number(o.amount) : Number(i.fee);
      return {
        name: i.name,
        systemFee: Number(i.fee || 0),
        amount: effectiveAmount,
        paidDirectly,
        note: o?.note || "",
        hasOverride,
      };
    });
    // Include override-only inspectors not present in by_inspector
    Object.entries(overrides).forEach(([name, o]) => {
      if (!rows.find(r => r.name === name)) {
        rows.push({
          name,
          systemFee: 0,
          amount: Number(o.amount || 0),
          paidDirectly: !!o.paid_directly_by_inspector,
          note: o.note || "",
          hasOverride: true,
        });
      }
    });
    rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  };

  const printTransmissionReport = (contractor, op, wd) => {
    const logoUrl = window.location.origin + "/aarifi-logo-full.png";
    const ov = getOverrides(wd.week_start);
    const rows = computeEffectiveByInspector(op, ov);
    const extras = ov.extra_items || [];
    const payableRows = rows.filter(r => !r.paidDirectly);
    const payableTotalUsd = payableRows.reduce((s, r) => s + r.amount, 0);
    const extrasUsd = extras.filter(e => e.currency === "USD").reduce((s, e) => s + e.amount, 0);
    const extrasBrl = extras.filter(e => e.currency === "BRL").reduce((s, e) => s + e.amount, 0);
    const grandUsd = payableTotalUsd + extrasUsd;
    const hasDirect = rows.some(r => r.paidDirectly);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Transmission Report - ${contractor}</title>
<style>
@page { margin: 15mm; }
body { font-family: 'Segoe UI', Arial, sans-serif; color: #1F2937; font-size: 11px; line-height: 1.5; }
.header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #2F5D8A; padding-bottom: 15px; }
.header img { height: 48px; margin-bottom: 8px; }
.header h1 { font-size: 16px; color: #2F5D8A; margin: 4px 0; font-weight: 700; }
.header h2 { font-size: 13px; color: #1F2937; margin: 2px 0; }
.header p { font-size: 10px; color: #666; max-width: 520px; margin: 8px auto 0; }
.period { background: #F8FAFB; border: 1px solid #E5E7EB; border-radius: 6px; padding: 10px 16px; margin: 12px 0; display: flex; justify-content: space-between; align-items: center; }
.period .label { font-size: 9px; text-transform: uppercase; color: #888; font-weight: 600; }
.period .value { font-size: 16px; font-weight: 700; color: #2F5D8A; }
.period .value.gold { color: #B08A3E; }
.section-title { font-size: 10px; text-transform: uppercase; font-weight: 700; color: #888; letter-spacing: 0.5px; margin: 16px 0 6px; }
table { width: 100%; border-collapse: collapse; margin: 8px 0; }
th { background: #F3F4F6; padding: 6px 10px; text-align: left; font-size: 9px; text-transform: uppercase; color: #666; font-weight: 600; border-bottom: 2px solid #E5E7EB; }
td { padding: 5px 10px; border-bottom: 1px solid #F3F4F6; font-size: 10px; vertical-align: top; }
.text-right { text-align: right; }
.bold { font-weight: 700; }
.gold { color: #B08A3E; }
.blue { color: #2F5D8A; }
.muted { color: #9CA3AF; font-style: italic; font-size: 9px; }
.star-row td { color: #92400E; }
.total-row { background: #F8FAFB; font-weight: 700; }
.total-row td { border-top: 2px solid #2F5D8A; padding: 8px 10px; }
.grand-row { background: #FEF3C7; font-weight: 800; font-size: 12px; }
.grand-row td { border-top: 3px double #B08A3E; padding: 10px; color: #7A5A1E; }
.brl-row { background: #EFF6FF; font-weight: 700; }
.brl-row td { border-top: 2px solid #2F5D8A; padding: 8px 10px; color: #1E40AF; }
.financial-note { margin: 20px 0; padding: 12px 16px; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 6px; font-size: 9px; color: #92400E; }
.footer-note { margin: 20px 0; padding: 10px 0; border-top: 1px solid #E5E7EB; font-size: 9px; color: #888; text-align: center; }
.signatures { display: flex; justify-content: space-between; margin-top: 40px; }
.sig-block { width: 45%; }
.sig-line { border-top: 1px solid #1F2937; margin-top: 40px; padding-top: 4px; font-size: 10px; font-weight: 600; }
.sig-date { font-size: 9px; color: #888; margin-top: 4px; }
.legend { margin-top: 6px; font-size: 9px; color: #92400E; }
</style></head><body>
<div class="header">
  <img src="${logoUrl}" alt="AARIFI" />
  <h1>AARIFI Inspection Services LLC</h1>
  <h2>Transmission Payment Report &mdash; ${contractor} (PJ)</h2>
  <p>This report summarizes all transmission services performed during the selected period, including inspector pass-through fees and additional office services.</p>
</div>
<div class="period">
  <div><span class="label">Period</span><br><span class="value">${wd.week_start} to ${wd.week_end}</span></div>
  <div><span class="label">Total Transmitted</span><br><span class="value">${op.total_orders} orders</span></div>
  <div><span class="label">To Pay (USD)</span><br><span class="value gold">$${grandUsd.toFixed(2)}</span></div>
  ${extrasBrl > 0 ? `<div><span class="label">Additional (BRL)</span><br><span class="value blue">R$${extrasBrl.toFixed(2)}</span></div>` : ''}
</div>
<p class="section-title">Transmission Fees by Inspector (pass-through)</p>
<table><thead><tr><th>Inspector</th><th>Note</th><th class="text-right">Amount (USD)</th></tr></thead><tbody>
${rows.map(r => `<tr class="${r.paidDirectly ? 'star-row' : ''}"><td>${r.name}${r.paidDirectly ? ' *' : ''}</td><td class="muted">${r.note || (r.paidDirectly ? 'Inspector will pay directly' : (r.hasOverride ? 'Adjusted per inspector PDF' : ''))}</td><td class="text-right bold gold">${r.paidDirectly ? '—' : '$' + r.amount.toFixed(2)}</td></tr>`).join("")}
<tr class="total-row"><td colspan="2">SUBTOTAL — TRANSMISSIONS</td><td class="text-right gold">$${payableTotalUsd.toFixed(2)}</td></tr>
</tbody></table>
${hasDirect ? `<p class="legend">* Inspector will pay Vitória directly. Amount is informational only and is NOT included in the total to pay.</p>` : ''}

${extras.length > 0 ? `
<p class="section-title">Additional Items</p>
<table><thead><tr><th>Description</th><th class="text-right">Amount</th></tr></thead><tbody>
${extras.map(e => `<tr><td>${e.label}</td><td class="text-right bold ${e.currency === 'BRL' ? 'blue' : 'gold'}">${e.currency === 'BRL' ? 'R$' : '$'}${e.amount.toFixed(2)}</td></tr>`).join("")}
${extrasUsd > 0 ? `<tr class="total-row"><td>SUBTOTAL — USD EXTRAS</td><td class="text-right gold">$${extrasUsd.toFixed(2)}</td></tr>` : ''}
${extrasBrl > 0 ? `<tr class="brl-row"><td>SUBTOTAL — BRL</td><td class="text-right">R$${extrasBrl.toFixed(2)}</td></tr>` : ''}
</tbody></table>` : ''}

<table><tbody>
<tr class="grand-row"><td>TOTAL TO PAY — USD</td><td class="text-right">$${grandUsd.toFixed(2)}</td></tr>
${extrasBrl > 0 ? `<tr class="brl-row"><td>TOTAL TO PAY — BRL</td><td class="text-right">R$${extrasBrl.toFixed(2)}</td></tr>` : ''}
</tbody></table>

<div class="financial-note"><strong>Financial Note:</strong> Transmission fees match the amounts already deducted from each inspector's weekly payment statement. Amounts flagged with (*) indicate the inspector will pay the contractor directly and have been excluded from AARIFI's payable total.</div>
<div class="footer-note">This report was prepared based on operational data recorded within the AARIFI system and transmission logs.</div>
<div class="signatures">
  <div class="sig-block"><div class="sig-line">Prepared By:</div><div class="sig-date">Date: _______________</div></div>
  <div class="sig-block"><div class="sig-line">Approved By (${contractor}):</div><div class="sig-date">Date: _______________</div></div>
</div></body></html>`;
    const pw = window.open("", "_blank");
    pw.document.write(html);
    pw.document.close();
    setTimeout(() => pw.print(), 500);
  };

  const data = weekData[expandedWeek];
  const operators = data?.operators || {};

  // ── OPERATOR DETAIL VIEW ──
  if (selectedOperator && operators[selectedOperator]) {
    const op = operators[selectedOperator];
    const isPaid = op.payment_status?.status === "confirmed";
    const recon = data?.reconciliation_summary || {};
    const isBalanced = recon.is_balanced !== false;
    const ws = data?.week_start;
    const ov = getOverrides(ws);
    const effRows = computeEffectiveByInspector(op, ov);
    const effPayableTotal = effRows.filter(r => !r.paidDirectly).reduce((s, r) => s + r.amount, 0);
    const effExtrasUsd = (ov.extra_items || []).filter(e => e.currency === "USD").reduce((s, e) => s + e.amount, 0);
    const effExtrasBrl = (ov.extra_items || []).filter(e => e.currency === "BRL").reduce((s, e) => s + e.amount, 0);
    const grandUsd = effPayableTotal + effExtrasUsd;

    const updateInspectorOverride = (name, patch) => {
      const current = ov.inspector_overrides[name] || { amount: effRows.find(r => r.name === name)?.systemFee || 0, paid_directly_by_inspector: false, note: "" };
      setOverrides(ws, {
        inspector_overrides: { ...ov.inspector_overrides, [name]: { ...current, ...patch } },
      });
    };

    const removeInspectorOverride = (name) => {
      const { [name]: _removed, ...rest } = ov.inspector_overrides;
      setOverrides(ws, { inspector_overrides: rest });
    };

    const addExtraItem = () => {
      const newItem = { id: `x${Date.now()}`, label: "New item", amount: 0, currency: "USD" };
      setOverrides(ws, { extra_items: [...(ov.extra_items || []), newItem] });
    };

    const updateExtraItem = (id, patch) => {
      setOverrides(ws, {
        extra_items: (ov.extra_items || []).map(e => e.id === id ? { ...e, ...patch } : e),
      });
    };

    const removeExtraItem = (id) => {
      setOverrides(ws, { extra_items: (ov.extra_items || []).filter(e => e.id !== id) });
    };

    return (
      <div className="space-y-4" data-testid="transmission-detail">
        <button onClick={() => setSelectedOperator(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#2F5D8A]" data-testid="back-from-operator"><ArrowLeft className="h-4 w-4" /> Back to {fmtRange(weeks[expandedWeek]?.start, weeks[expandedWeek]?.end)}</button>
        <div><p className="text-sm font-semibold text-[#1F2937]">{selectedOperator} — Contractor Service Detail</p><p className="text-[10px] text-gray-400">{data?.week_start} — {data?.week_end}</p></div>
        {message && <MsgBar message={message} onClose={() => setMessage(null)} />}

        {/* Reconciliation Banner */}
        <div
          className={`rounded-lg border-2 px-4 py-3 flex items-start gap-3 ${
            isBalanced
              ? "bg-[#6B8F62]/8 border-[#6B8F62]/40"
              : "bg-[#DC2626]/8 border-[#DC2626]/50"
          }`}
          data-testid="reconciliation-banner"
        >
          <div className={`p-2 rounded-full flex-shrink-0 ${isBalanced ? "bg-[#6B8F62]/20" : "bg-[#DC2626]/20"}`}>
            {isBalanced
              ? <CheckCircle className="h-5 w-5 text-[#6B8F62]" />
              : <AlertCircle className="h-5 w-5 text-[#DC2626]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${isBalanced ? "text-[#5A7D52]" : "text-[#8B1A1A]"}`}>
              {isBalanced
                ? "Reconciliation Balanced — Charge = Payout (pass-through verified)"
                : "RECONCILIATION ERROR — Mismatch detected, payment blocked"}
            </p>
            <div className="mt-2 grid grid-cols-3 gap-3">
              <div>
                <p className="text-[9px] uppercase font-semibold text-gray-500 tracking-wider">Charged to Inspectors</p>
                <p className="text-lg font-bold text-[#B08A3E]" data-testid="recon-total-charged">${(recon.total_charged_to_inspectors || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase font-semibold text-gray-500 tracking-wider">Paid to {selectedOperator}</p>
                <p className="text-lg font-bold text-[#2F5D8A]" data-testid="recon-total-paid">${(recon.total_paid_to_vitoria || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase font-semibold text-gray-500 tracking-wider">Difference</p>
                <p className={`text-lg font-bold ${isBalanced ? "text-[#6B8F62]" : "text-[#DC2626]"}`} data-testid="recon-diff">
                  ${Math.abs(recon.difference || 0).toFixed(2)}
                </p>
              </div>
            </div>
            {!isBalanced && recon.error && (
              <p className="text-xs text-[#8B1A1A] mt-2 font-semibold" data-testid="recon-error">{recon.error}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3"><p className="text-[10px] uppercase text-gray-400 font-semibold">Total Transmitted</p><p className="text-2xl font-bold text-[#1F2937]">{op.total_orders}</p></div>
          <div className="bg-white rounded-lg border border-[#B08A3E]/30 px-4 py-3"><p className="text-[10px] uppercase text-[#B08A3E] font-semibold">Total Payment</p><p className="text-2xl font-bold text-[#B08A3E]">${(op.total_fee || 0).toFixed(2)}</p></div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3"><p className="text-[10px] uppercase text-gray-400 font-semibold">Inspectors</p><p className="text-2xl font-bold text-[#2F5D8A]">{op.by_inspector.length}</p></div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3"><p className="text-[10px] uppercase text-gray-400 font-semibold">Payment Status</p><p className={`text-lg font-bold ${isPaid ? "text-[#6B8F62]" : "text-[#B08A3E]"}`}>{isPaid ? "Confirmed" : "Pending"}</p></div>
        </div>
        {!isPaid && (
          <button
            onClick={() => confirmPayment(selectedOperator)}
            disabled={confirming || !isBalanced}
            title={!isBalanced ? "Cannot confirm payment while reconciliation is unbalanced" : ""}
            className={`px-4 py-2 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50 ${
              isBalanced ? "bg-[#6B8F62] hover:bg-[#5A7D52]" : "bg-gray-300 cursor-not-allowed"
            }`}
            data-testid="confirm-transmission-payment"
          >
            <CheckCircle className="h-3.5 w-3.5" />
            {confirming ? "Confirming..." : isBalanced ? "Confirm Service Payment" : "Blocked — Reconciliation Mismatch"}
          </button>
        )}
        <button onClick={() => printTransmissionReport(selectedOperator, op, data)} className="px-4 py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="print-transmission-report"><Printer className="h-3.5 w-3.5" /> Print Report</button>

        <div className="flex items-center gap-2">
          <input ref={discFileRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={e => handleDiscrepancyFile(e, selectedOperator, data.week_start, data.week_end)}
            data-testid="discrepancy-file-input" />
          <button onClick={() => discFileRef.current?.click()} disabled={generatingDisc}
            className="px-4 py-2 bg-[#C07070] hover:bg-[#A55858] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            data-testid="generate-discrepancy-pdf-btn"
            title={`Upload ${selectedOperator}'s transmission sheet to cross-check against AARIFI closings and download a discrepancy PDF`}>
            <FileText className="h-3.5 w-3.5" />
            {generatingDisc ? "Generating..." : "Discrepancy PDF"}
          </button>
          <span className="text-[10px] text-gray-400">Upload {selectedOperator}'s sheet to compare vs AARIFI closings</span>
        </div>
        {op.by_day.length > 0 && (
          <div><p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">By Day</p>
            <div className="flex gap-2 flex-wrap">{op.by_day.map(d => (
              <div key={d.date} className="px-3 py-2 bg-white rounded-lg border border-gray-200"><p className="text-[10px] text-gray-400">{d.date}</p><p className="text-sm font-bold text-[#B08A3E]">${d.fee.toFixed(2)}</p></div>
            ))}</div>
          </div>
        )}
        {/* Adjustments Panel */}
        <div className="bg-white rounded-lg border border-[#B08A3E]/30 p-4 space-y-3" data-testid="adjustments-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-sm font-bold text-[#7A5A1E]">Adjustments for {selectedOperator}'s Receipt</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Override per-inspector amount (to match the PDF already delivered), mark "paid directly by inspector", and add extra line items.</p>
            </div>
            <button onClick={() => saveOverrides(ws)} disabled={savingOverrides}
              className="px-3 py-1.5 bg-[#B08A3E] hover:bg-[#8B6D2E] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="save-overrides-btn">
              <Save className="h-3.5 w-3.5" />
              {savingOverrides ? "Saving..." : "Save Adjustments"}
            </button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="adjustments-inspector-table">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <TH>Inspector</TH>
                  <TH align="right">System ($)</TH>
                  <TH align="right">Final ($)</TH>
                  <TH>Paid directly?</TH>
                  <TH>Note</TH>
                </tr>
              </thead>
              <tbody>
                {effRows.map(r => {
                  const changed = r.hasOverride && Math.abs(r.amount - r.systemFee) > 0.001;
                  return (
                    <tr key={r.name} className={`border-b border-gray-100 ${r.paidDirectly ? "bg-[#D1A054]/5" : ""}`} data-testid={`adj-row-${r.name}`}>
                      <td className="px-3 py-2 text-gray-800 font-medium text-xs">
                        {r.name}{r.paidDirectly && <span className="ml-1 text-[#B08A3E] font-bold">*</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-gray-400">${r.systemFee.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" step="0.01" value={r.amount}
                          onChange={e => updateInspectorOverride(r.name, { amount: parseFloat(e.target.value) || 0 })}
                          className={`w-24 px-2 py-1 text-xs text-right border rounded focus:outline-none focus:border-[#B08A3E] font-bold ${changed ? "border-[#B08A3E] bg-[#FEF7E5] text-[#8B6D2E]" : "border-gray-200 text-[#1F2937]"}`}
                          data-testid={`adj-amount-${r.name}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                          <input
                            type="checkbox" checked={r.paidDirectly}
                            onChange={e => updateInspectorOverride(r.name, { paid_directly_by_inspector: e.target.checked })}
                            className="h-3.5 w-3.5 accent-[#B08A3E]"
                            data-testid={`adj-direct-${r.name}`}
                          />
                          <span className={r.paidDirectly ? "text-[#B08A3E] font-semibold" : "text-gray-500"}>
                            {r.paidDirectly ? "Paid by inspector" : "Via AARIFI"}
                          </span>
                        </label>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text" value={r.note} placeholder="Optional note"
                            onChange={e => updateInspectorOverride(r.name, { note: e.target.value })}
                            className="flex-1 px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-[#B08A3E]"
                            data-testid={`adj-note-${r.name}`}
                          />
                          {r.hasOverride && (
                            <button onClick={() => removeInspectorOverride(r.name)}
                              className="p-1 text-gray-300 hover:text-[#DC2626]" title="Reset to system value"
                              data-testid={`adj-reset-${r.name}`}>
                              <RefreshCw className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#B08A3E]/40 bg-[#FEF7E5] font-bold">
                  <td className="px-3 py-2 text-[11px] uppercase text-[#7A5A1E]" colSpan={2}>Subtotal (excluding direct-pay)</td>
                  <td className="px-3 py-2 text-right text-[#B08A3E]" data-testid="adj-subtotal">${effPayableTotal.toFixed(2)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Extra items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Extra items on receipt</p>
              <button onClick={addExtraItem}
                className="px-2 py-1 bg-[#2F5D8A]/10 text-[#2F5D8A] hover:bg-[#2F5D8A]/20 text-[10px] font-semibold rounded flex items-center gap-1"
                data-testid="add-extra-item-btn">
                <Plus className="h-3 w-3" /> Add line
              </button>
            </div>
            <div className="space-y-1.5">
              {(ov.extra_items || []).length === 0 ? (
                <p className="text-[11px] text-gray-400 italic px-2">No extra items. Use "Add line" to include Office Transmissions, Office Work (BRL), etc.</p>
              ) : ov.extra_items.map(item => (
                <div key={item.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5 border border-gray-200" data-testid={`extra-row-${item.id}`}>
                  <input
                    type="text" value={item.label} placeholder="Description"
                    onChange={e => updateExtraItem(item.id, { label: e.target.value })}
                    className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-[#B08A3E] bg-white"
                    data-testid={`extra-label-${item.id}`}
                  />
                  <input
                    type="number" step="0.01" value={item.amount}
                    onChange={e => updateExtraItem(item.id, { amount: parseFloat(e.target.value) || 0 })}
                    className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:outline-none focus:border-[#B08A3E] font-bold bg-white"
                    data-testid={`extra-amount-${item.id}`}
                  />
                  <select value={item.currency}
                    onChange={e => updateExtraItem(item.id, { currency: e.target.value })}
                    className="px-2 py-1 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:border-[#B08A3E]"
                    data-testid={`extra-currency-${item.id}`}>
                    <option value="USD">USD</option>
                    <option value="BRL">BRL</option>
                  </select>
                  <button onClick={() => removeExtraItem(item.id)}
                    className="p-1 text-gray-300 hover:text-[#DC2626]" title="Remove"
                    data-testid={`extra-remove-${item.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Grand totals */}
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
            <div className="bg-[#FEF7E5] rounded-lg px-3 py-2 border border-[#B08A3E]/30">
              <p className="text-[9px] font-bold text-[#B08A3E] uppercase tracking-wider">Total to Pay — USD</p>
              <p className="text-xl font-extrabold text-[#8B6D2E]" data-testid="grand-total-usd">${grandUsd.toFixed(2)}</p>
              <p className="text-[9px] text-gray-500">Transmissions ${effPayableTotal.toFixed(2)} + Extras USD ${effExtrasUsd.toFixed(2)}</p>
            </div>
            {effExtrasBrl > 0 && (
              <div className="bg-[#EFF6FF] rounded-lg px-3 py-2 border border-[#2F5D8A]/30">
                <p className="text-[9px] font-bold text-[#2F5D8A] uppercase tracking-wider">Additional — BRL</p>
                <p className="text-xl font-extrabold text-[#1E40AF]" data-testid="grand-total-brl">R$ {effExtrasBrl.toFixed(2)}</p>
                <p className="text-[9px] text-gray-500">Paid separately in BRL</p>
              </div>
            )}
          </div>
        </div>

        {/* Reconciliation Detail (per-order) */}
        {op.reconciliation && op.reconciliation.length > 0 && (
          <div data-testid="reconciliation-detail-section">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold text-gray-400 uppercase">Reconciliation Detail (per order)</p>
              <p className="text-[10px] text-gray-400">
                {op.reconciliation.length} orders · must always balance to $0.00
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="reconciliation-detail-table">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      <TH>Order #</TH>
                      <TH>Inspector</TH>
                      <TH>Date</TH>
                      <TH align="right">Charged</TH>
                      <TH align="right">Paid to {selectedOperator}</TH>
                      <TH align="right">Diff</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {op.reconciliation.map((r, i) => {
                      const diff = r.difference || 0;
                      const diffOk = Math.abs(diff) < 0.01;
                      return (
                        <tr key={`${r.order_number}-${i}`} className={`border-b border-gray-50 ${diffOk ? "" : "bg-[#DC2626]/[0.05]"}`} data-testid={`recon-row-${r.order_number}`}>
                          <td className="px-3 py-1.5 font-medium text-gray-700">{r.order_number}</td>
                          <td className="px-3 py-1.5 text-gray-700">{r.inspector || "—"}</td>
                          <td className="px-3 py-1.5 text-gray-500">{r.route_date || "—"}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-[#B08A3E]">${(r.charged_to_inspector || 0).toFixed(2)}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-[#2F5D8A]">${(r.paid_to_vitoria || 0).toFixed(2)}</td>
                          <td className={`px-3 py-1.5 text-right font-bold ${diffOk ? "text-[#6B8F62]" : "text-[#DC2626]"}`}>
                            ${Math.abs(diff).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 sticky bottom-0">
                    <tr className="border-t-2 border-gray-300 font-bold">
                      <td colSpan={3} className="px-3 py-2 text-right text-gray-600 uppercase text-[10px]">Totals</td>
                      <td className="px-3 py-2 text-right text-[#B08A3E]" data-testid="recon-footer-charged">${(recon.total_charged_to_inspectors || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-[#2F5D8A]" data-testid="recon-footer-paid">${(recon.total_paid_to_vitoria || 0).toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right ${isBalanced ? "text-[#6B8F62]" : "text-[#DC2626]"}`} data-testid="recon-footer-diff">${Math.abs(recon.difference || 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── WEEK CARDS ──
  return (
    <div className="space-y-4" data-testid="transmission-payment-module">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contractor Transmission Payment</p>
      <p className="text-gray-400 text-xs">Week → Operator → Day → Detail</p>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}
      <div className="space-y-3">
        {weeks.map((w, i) => {
          const expanded = expandedWeek === i;
          const wd = weekData[i];
          const ops = wd?.operators || {};
          const totalOrders = Object.values(ops).reduce((s, o) => s + o.total_orders, 0);
          const totalFee = Object.values(ops).reduce((s, o) => s + (o.total_fee || 0), 0);
          return (
            <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`trans-week-${i}`}>
              <button onClick={() => toggleWeek(i)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left" data-testid={`trans-week-toggle-${i}`}>
                <div className="flex items-center gap-4">
                  <p className="text-sm font-semibold text-[#1F2937]">{fmtRange(w.start, w.end)}</p>
                  {wd && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#2F5D8A]/10 text-[#2F5D8A]">{totalOrders} transmissions · ${totalFee.toFixed(2)}</span>}
                </div>
                <div className="flex items-center gap-6 text-xs">
                  {wd && <span className="text-gray-500">Operators: <span className="font-bold">{Object.keys(ops).length}</span></span>}
                  {expanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                </div>
              </button>
              {expanded && (
                <div className="border-t border-gray-200 px-5 py-4 space-y-2">
                  {loadingWeek === i ? <Spinner /> : !wd ? null : Object.keys(ops).length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">No transmission data for this week.</div>
                  ) : Object.entries(ops).map(([name, op]) => {
                    const isPaid = op.payment_status?.status === "confirmed";
                    return (
                      <button key={name} onClick={() => setSelectedOperator(name)} className={`w-full bg-gray-50/60 rounded-lg border p-4 text-left hover:border-[#2F5D8A] transition-all group ${isPaid ? "border-[#6B8F62]/40" : "border-gray-200"}`} data-testid={`operator-card-${name}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-gray-400 group-hover:text-[#2F5D8A]" />
                            <div><p className="text-sm font-semibold text-[#1F2937] group-hover:text-[#2F5D8A]">{name}</p><p className="text-[10px] text-gray-400">{op.by_inspector.length} inspectors · {op.by_day.length} days</p></div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-500">{op.total_orders} orders · <span className="font-bold text-[#B08A3E]">${(op.total_fee || 0).toFixed(2)}</span></span>
                            {isPaid ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#6B8F62]/12 text-[#6B8F62]">Confirmed</span> : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#B08A3E]/12 text-[#B08A3E]">Pending</span>}
                            <ChevronRightIcon className="h-4 w-4 text-gray-300 group-hover:text-[#2F5D8A]" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   OPERATIONAL PAYMENT MODULE — WEEK CARDS → DAY BREAKDOWN
   ════════════════════════════════════════════════════════ */
const OperationalPaymentModule = () => {
  const weeks = useMemo(() => generateWeekPeriods(4), []);
  const [expandedWeek, setExpandedWeek] = useState(-1);
  const [weekData, setWeekData] = useState({});
  const [loadingWeek, setLoadingWeek] = useState(-1);
  const [showExcluded, setShowExcluded] = useState(false);

  const loadWeek = useCallback(async (idx) => {
    setLoadingWeek(idx);
    try { const res = await axios.get(`${API}/finance/operational-payment?week_start=${weeks[idx].start}`); setWeekData(prev => ({ ...prev, [idx]: res.data })); }
    catch {} finally { setLoadingWeek(-1); }
  }, [weeks]);

  const toggleWeek = (idx) => {
    if (expandedWeek === idx) { setExpandedWeek(-1); return; }
    setExpandedWeek(idx); setShowExcluded(false);
    if (!weekData[idx]) loadWeek(idx);
  };

  return (
    <div className="space-y-4" data-testid="operational-payment-module">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contractor Service Payment</p>
      <p className="text-gray-400 text-xs">Week → Day → Orders</p>
      <div className="space-y-3">
        {weeks.map((w, i) => {
          const expanded = expandedWeek === i;
          const wd = weekData[i];
          return (
            <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`op-week-${i}`}>
              <button onClick={() => toggleWeek(i)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left" data-testid={`op-week-toggle-${i}`}>
                <div className="flex items-center gap-4">
                  <p className="text-sm font-semibold text-[#1F2937]">{fmtRange(w.start, w.end)}</p>
                  {wd && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#6B8F62]/10 text-[#6B8F62]">{wd.total_valid_orders} orders</span>}
                </div>
                <div className="flex items-center gap-6 text-xs">
                  {wd && <>
                    <span className="text-gray-500">{wd.operator}: <span className="text-[#2F5D8A] font-bold">${wd.total_payment}</span></span>
                    <span className="text-gray-500">Rate: <span className="font-bold">${wd.rate_per_order}/order</span></span>
                  </>}
                  {expanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                </div>
              </button>
              {expanded && (
                <div className="border-t border-gray-200 px-5 py-4 space-y-4">
                  {loadingWeek === i ? <Spinner /> : !wd ? null : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="bg-[#6B8F62]/8 border border-[#6B8F62]/25 rounded-lg p-4"><p className="text-[10px] uppercase font-semibold text-[#6B8F62]">Valid Orders</p><p className="text-2xl font-bold text-[#6B8F62] mt-1">{wd.total_valid_orders}</p><p className="text-xs text-gray-400 mt-0.5">Completed, no flag</p></div>
                        <div className="bg-[#B08A3E]/8 border border-[#B08A3E]/25 rounded-lg p-4"><p className="text-[10px] uppercase font-semibold text-[#B08A3E]">Rate</p><p className="text-2xl font-bold text-[#B08A3E] mt-1">${wd.rate_per_order}/order</p></div>
                        <div className="bg-[#2F5D8A]/8 border border-[#2F5D8A]/25 rounded-lg p-4"><p className="text-[10px] uppercase font-semibold text-[#2F5D8A]">Total Payment</p><p className="text-2xl font-bold text-[#2F5D8A] mt-1">${wd.total_payment}</p></div>
                      </div>
                      {wd.by_day.length > 0 && (
                        <div><p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">By Day</p>
                          <div className="flex gap-2 flex-wrap">{wd.by_day.map(d => (
                            <div key={d.date} className="px-3 py-2 bg-gray-50/60 rounded-lg border border-gray-200"><p className="text-[10px] text-gray-400">{d.date}</p><p className="text-sm font-bold text-[#1F2937]">{d.count} orders</p></div>
                          ))}</div>
                        </div>
                      )}
                      {wd.total_excluded > 0 && (
                        <div className="bg-[#C07070]/5 border border-[#C07070]/20 rounded-lg p-3">
                          <button onClick={() => setShowExcluded(!showExcluded)} className="flex items-center gap-2 w-full text-left" data-testid="show-excluded-btn">
                            <Flag className="h-3.5 w-3.5 text-[#C07070]" />
                            <span className="text-xs font-semibold text-[#C07070]">{wd.total_excluded} orders excluded</span>
                            <span className="text-[10px] text-gray-400">(follow-up, cancelled, returned, rescheduled)</span>
                            {showExcluded ? <ChevronUp className="h-3 w-3 text-gray-400 ml-auto" /> : <ChevronDown className="h-3 w-3 text-gray-400 ml-auto" />}
                          </button>
                          {showExcluded && (
                            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">{wd.excluded_orders.map((e, idx) => (
                              <div key={idx} className="flex items-center gap-3 text-xs px-2 py-1 bg-white rounded">
                                <span className="text-gray-800 font-medium">{e.order_number || e.order_id?.slice(-6)}</span>
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#C07070]/10 text-[#C07070]">{e.action}</span>
                                {e.follow_up && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#C07070]/20 text-[#C07070]">Flagged</span>}
                                <span className="text-gray-400">{e.inspector}</span>
                              </div>
                            ))}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   OFFICE EXPENSES MODULE
   ════════════════════════════════════════════════════════ */
const OfficeExpensesModule = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ employee_name: "", role: "", salary_amount: "", frequency: "Weekly" });
  const [message, setMessage] = useState(null);
  // Extra expenses
  const [extraExpenses, setExtraExpenses] = useState([]);
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [extraForm, setExtraForm] = useState({ description: "", amount: "", week_start: "" });
  const weekOptions = useMemo(() => generateWeekPeriods(8).map(w => w.start), []);

  const fetchExpenses = useCallback(async () => {
    try {
      const [res, extraRes] = await Promise.all([
        axios.get(`${API}/finance/office-expenses`),
        axios.get(`${API}/finance/extra-expenses`),
      ]);
      setExpenses(res.data);
      setExtraExpenses(extraRes.data);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const totalWeekly = useMemo(() => expenses.reduce((sum, e) => { const amt = parseFloat(e.salary_amount) || 0; return sum + (e.frequency === "Weekly" ? amt : amt * 12 / 52); }, 0), [expenses]);

  const handleSave = async () => {
    if (!form.employee_name || !form.salary_amount) { setMessage({ type: "error", text: "Name and amount required" }); return; }
    const payload = { ...form, salary_amount: parseFloat(form.salary_amount) };
    try {
      if (editing) { await axios.put(`${API}/finance/office-expenses/${editing}`, payload); setMessage({ type: "success", text: "Updated" }); }
      else { await axios.post(`${API}/finance/office-expenses`, payload); setMessage({ type: "success", text: "Created" }); }
      setShowForm(false); setEditing(null); setForm({ employee_name: "", role: "", salary_amount: "", frequency: "Weekly" }); fetchExpenses();
    } catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };
  const handleEdit = (exp) => { setForm({ employee_name: exp.employee_name, role: exp.role, salary_amount: String(exp.salary_amount), frequency: exp.frequency }); setEditing(exp.id); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/finance/office-expenses/${id}`); fetchExpenses(); setMessage({ type: "success", text: "Deleted" }); } catch { setMessage({ type: "error", text: "Delete failed" }); } };

  return (
    <div className="space-y-4" data-testid="office-expenses-module">
      <div className="flex items-center justify-between">
        <div><p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Office Expenses</p><p className="text-gray-400 text-xs mt-0.5">Fixed contractor costs (weekly total: <span className="text-[#B08A3E] font-bold">${totalWeekly.toFixed(2)}</span>)</p></div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ employee_name: "", role: "", salary_amount: "", frequency: "Weekly" }); }} className="px-3 py-1.5 bg-[#B08A3E] hover:bg-[#9A7A3E] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5" data-testid="add-expense-btn"><Plus className="h-3.5 w-3.5" /> Add Expense</button>
      </div>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}
      {showForm && (
        <div className="bg-white border border-[#D1A054]/30 rounded-lg p-4 space-y-3" data-testid="expense-form">
          <p className="text-sm font-semibold text-gray-800">{editing ? "Edit Expense" : "New Office Expense"}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FormInput label="Contractor Name" value={form.employee_name} onChange={v => setForm(p => ({ ...p, employee_name: v }))} placeholder="Name" testId="expense-name-input" />
            <FormInput label="Role" value={form.role} onChange={v => setForm(p => ({ ...p, role: v }))} placeholder="Role" testId="expense-role-input" />
            <FormInput label="Service Fee ($)" value={form.salary_amount} onChange={v => setForm(p => ({ ...p, salary_amount: v }))} placeholder="0.00" type="number" testId="expense-amount-input" />
            <FormSelect label="Frequency" value={form.frequency} onChange={v => setForm(p => ({ ...p, frequency: v }))} options={["Weekly", "Monthly"]} testId="expense-frequency-select" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={handleSave} className="px-4 py-2 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5" data-testid="expense-save-btn"><Save className="h-3.5 w-3.5" />{editing ? "Update" : "Save"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 text-gray-500 hover:text-gray-800 text-sm">Cancel</button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? <Spinner /> : expenses.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No office expenses configured.</div>
        ) : (
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead><tr className="border-b border-gray-200"><TH>Contractor</TH><TH>Role</TH><TH align="right">Service Fee</TH><TH>Frequency</TH><TH align="right">Weekly Equiv.</TH><TH align="right">Actions</TH></tr></thead>
            <tbody>{expenses.map(exp => {
              const amt = parseFloat(exp.salary_amount) || 0;
              const weeklyEq = exp.frequency === "Weekly" ? amt : amt * 12 / 52;
              return (
                <tr key={exp.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`expense-row-${exp.id}`}>
                  <td className="px-4 py-2.5 text-gray-800 font-medium">{exp.employee_name}</td>
                  <td className="px-4 py-2.5 text-gray-600">{exp.role}</td>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${amt.toFixed(2)}</td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${exp.frequency === "Weekly" ? "bg-[#2F5D8A]/12 text-[#2F5D8A]" : "bg-[#6B5B8D]/12 text-[#5A4F7A]"}`}>{exp.frequency}</span></td>
                  <td className="px-4 py-2.5 text-right text-[#B08A3E] font-bold">${weeklyEq.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right"><ActionBtns onEdit={() => handleEdit(exp)} onDelete={() => handleDelete(exp.id)} id={exp.id} prefix="expense" /></td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>

      {/* Extra Expenses Section */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <div><p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Extra Expenses</p><p className="text-gray-400 text-[10px] mt-0.5">One-time weekly expenses</p></div>
          <button onClick={() => { setShowExtraForm(true); setExtraForm({ description: "", amount: "", week_start: weekOptions[0] || "" }); }} className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5" data-testid="add-extra-expense-btn"><Plus className="h-3.5 w-3.5" /> Add Extra</button>
        </div>
        {showExtraForm && (
          <div className="bg-white border border-[#2F5D8A]/30 rounded-lg p-4 space-y-3 mb-3" data-testid="extra-expense-form">
            <p className="text-sm font-semibold text-gray-800">New Extra Expense</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormInput label="Description" value={extraForm.description} onChange={v => setExtraForm(p => ({ ...p, description: v }))} placeholder="Expense description" testId="extra-desc-input" />
              <FormInput label="Amount ($)" value={extraForm.amount} onChange={v => setExtraForm(p => ({ ...p, amount: v }))} placeholder="0.00" type="number" testId="extra-amount-input" />
              <div>
                <label className="text-[10px] uppercase text-gray-400 font-semibold block mb-1">Week</label>
                <select value={extraForm.week_start} onChange={e => setExtraForm(p => ({ ...p, week_start: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none" data-testid="extra-week-select">
                  {weekOptions.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button onClick={async () => {
                if (!extraForm.description || !extraForm.amount) { setMessage({ type: "error", text: "Description and amount required" }); return; }
                try { await axios.post(`${API}/finance/extra-expenses`, { ...extraForm, amount: parseFloat(extraForm.amount) }); setShowExtraForm(false); fetchExpenses(); setMessage({ type: "success", text: "Extra expense added" }); }
                catch (err) { setMessage({ type: "error", text: err.response?.data?.detail || "Failed" }); }
              }} className="px-4 py-2 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5" data-testid="extra-save-btn"><Save className="h-3.5 w-3.5" />Save</button>
              <button onClick={() => setShowExtraForm(false)} className="px-4 py-2 text-gray-500 hover:text-gray-800 text-sm">Cancel</button>
            </div>
          </div>
        )}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {extraExpenses.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No extra expenses.</div>
          ) : (
            <table className="w-full text-sm" data-testid="extra-expenses-table">
              <thead><tr className="border-b border-gray-200"><TH>Description</TH><TH>Week</TH><TH align="right">Amount</TH><TH>Added By</TH><TH align="right">Actions</TH></tr></thead>
              <tbody>{extraExpenses.map(exp => (
                <tr key={exp.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`extra-row-${exp.id}`}>
                  <td className="px-4 py-2.5 text-gray-800 font-medium">{exp.description}</td>
                  <td className="px-4 py-2.5 text-gray-600 text-xs">{exp.week_start}</td>
                  <td className="px-4 py-2.5 text-right text-[#2F5D8A] font-bold">${parseFloat(exp.amount).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">{exp.created_by}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={async () => { try { await axios.delete(`${API}/finance/extra-expenses/${exp.id}`); fetchExpenses(); setMessage({ type: "success", text: "Deleted" }); } catch { setMessage({ type: "error", text: "Delete failed" }); }}} className="p-1.5 rounded hover:bg-[#C07070]/20 text-gray-500 hover:text-[#8B4545]" data-testid={`delete-extra-${exp.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};


/* ════════════════════════════════════════════════════════
   RECONCILIATION MODULE — Tuesday Transmission Validation
   ════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════
   RECONCILIATION MODULE — 4-Card Audit Model
   ════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════
   PJ PAYMENT MODULE — Office Fixed Payments (Gabi/João)
   ════════════════════════════════════════════════════════ */
const PJPaymentModule = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [message, setMessage] = useState(null);
  // Office PJ hours panel — READ-ONLY, display only. No payment computation.
  const today0 = new Date().toISOString().slice(0, 10);
  const start15 = (() => { const d = new Date(); d.setDate(d.getDate() - 15); return d.toISOString().slice(0,10); })();
  const [hoursStart, setHoursStart] = useState(start15);
  const [hoursEnd, setHoursEnd] = useState(today0);
  const [hoursData, setHoursData] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchPJ = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/pj-payments`);
      setData(res.data);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchPJ(); }, [fetchPJ]);

  const fetchHours = useCallback(async () => {
    setHoursLoading(true);
    try {
      const res = await axios.get(`${API}/finance/pj-payments/with-hours`, { params: { start: hoursStart, end: hoursEnd } });
      setHoursData(res.data);
    } catch { setHoursData(null); } finally { setHoursLoading(false); }
  }, [hoursStart, hoursEnd]);
  useEffect(() => { fetchHours(); }, [fetchHours]);

  const today = new Date().toISOString().slice(0, 10);

  // Determine which period we're in (every 15 days from a base)
  const getPeriodLabel = () => {
    const d = new Date();
    const day = d.getDate();
    const month = d.toLocaleString("en", { month: "short" });
    return day <= 15 ? `1-15 ${month}` : `16-${new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()} ${month}`;
  };

  const handlePay = async (member) => {
    if (!window.confirm(`Mark ${member.name} as PAID $${member.amount.toFixed(2)} for ${getPeriodLabel()}?`)) return;
    setPaying(member.name);
    try {
      await axios.post(`${API}/finance/pj-payments/pay`, {
        name: member.name, amount: member.amount,
        payment_date: today, period_label: getPeriodLabel(),
      });
      setMessage({ type: "success", text: `${member.name} paid $${member.amount.toFixed(2)}` });
      fetchPJ();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed" });
    } finally { setPaying(null); }
  };

  const members = data?.members || [];
  const history = data?.history || [];

  // Check if paid today
  const isPaidToday = (name) => history.some(h => h.name === name && h.payment_date === today);

  return (
    <div className="space-y-4" data-testid="pj-payment-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Office (PJ) — Fixed Payments</p>
          <p className="text-gray-400 text-xs mt-0.5">Gabi & João — $400 every 15 days (biweekly PJ)</p>
        </div>
        <button onClick={fetchPJ} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}
      {loading ? <Spinner /> : (
        <>
          {/* Member Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {members.map(m => {
              const paid = isPaidToday(m.name);
              const lastPayment = history.find(h => h.name === m.name);
              return (
                <div key={m.name} className={`bg-white rounded-lg border p-4 ${paid ? "border-[#6B8F62]/30 bg-[#6B8F62]/[0.02]" : "border-gray-200"}`} data-testid={`pj-card-${m.name}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-lg font-bold text-gray-800">{m.name}</p>
                      <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider">{m.role} — PJ Contractor</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#B08A3E]">${m.amount.toFixed(2)}</p>
                      <p className="text-[10px] text-gray-400">Every {m.frequency_days} days</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                    <div className="text-xs text-gray-500">
                      {lastPayment ? (
                        <span>Last paid: <span className="font-medium text-gray-700">{lastPayment.payment_date}</span> ({lastPayment.period_label}) <span className="text-[10px] text-gray-400">by {lastPayment.paid_by}</span></span>
                      ) : (
                        <span className="text-gray-300">No payment history</span>
                      )}
                    </div>
                    {paid ? (
                      <span className="px-3 py-1 bg-[#6B8F62] text-white text-xs font-bold rounded" data-testid={`pj-paid-${m.name}`}>PAID {today}</span>
                    ) : (
                      <button onClick={() => handlePay(m)} disabled={paying === m.name}
                        className="px-4 py-1.5 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-semibold rounded flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`pj-pay-btn-${m.name}`}>
                        <DollarSign className="h-3.5 w-3.5" /> {paying === m.name ? "..." : "Mark as Paid"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Payment History */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Payment History</p>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-300 text-sm">No payments recorded yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-gray-200 bg-gray-50">
                    <TH>Name</TH><TH>Amount</TH><TH>Payment Date</TH><TH>Period</TH><TH>Paid By</TH><TH>Timestamp</TH>
                  </tr></thead>
                  <tbody>{history.map((h, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-800">{h.name}</td>
                      <td className="px-3 py-2 text-[#B08A3E] font-bold">${h.amount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-gray-600">{h.payment_date}</td>
                      <td className="px-3 py-2 text-gray-500">{h.period_label}</td>
                      <td className="px-3 py-2 text-gray-400">{h.paid_by}</td>
                      <td className="px-3 py-2 text-gray-300 text-[10px]">{h.paid_at ? new Date(h.paid_at).toLocaleString() : ""}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              Office PJ — Login / Hour Records (READ-ONLY display)
              Source: db.office_time_logs. No payment computation. No writes.
              ═══════════════════════════════════════════════════════════════ */}
          <div className="border-t border-gray-200 pt-6 mt-6" data-testid="pj-hours-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Office PJ — Login / Hour Records</p>
                <p className="text-gray-400 text-[11px] mt-0.5">Read-only view of clock-in/out, lunch, pauses & total hours. Not a payment calculation.</p>
              </div>
              <div className="flex items-end gap-2">
                <label className="text-[10px] text-gray-500 uppercase tracking-wider">Start
                  <input type="date" value={hoursStart} onChange={e => setHoursStart(e.target.value)}
                    className="ml-2 px-2 py-1 border border-gray-200 rounded text-xs" data-testid="pj-hours-start" />
                </label>
                <label className="text-[10px] text-gray-500 uppercase tracking-wider">End
                  <input type="date" value={hoursEnd} onChange={e => setHoursEnd(e.target.value)}
                    className="ml-2 px-2 py-1 border border-gray-200 rounded text-xs" data-testid="pj-hours-end" />
                </label>
                <button onClick={fetchHours} className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-semibold rounded flex items-center gap-1.5" data-testid="pj-hours-refresh">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              </div>
            </div>

            {hoursLoading ? <Spinner /> : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {!hoursData || (hoursData.roster || []).length === 0 ? (
                  <div className="text-center py-8 text-gray-300 text-sm">No login records found for this period.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-gray-200 bg-gray-50">
                      <TH>Name</TH><TH>Email</TH><TH>Type</TH>
                      <TH>Days</TH><TH>Login h</TH><TH>Lunch h</TH><TH>Pause h</TH><TH>Paid h</TH>
                      <TH>Open Sessions</TH><TH>&nbsp;</TH>
                    </tr></thead>
                    <tbody>
                      {(hoursData.roster || []).map((r, idx) => {
                        const tot = r.totals || {};
                        const key = r.user_id || r.name;
                        const isOpen = !!expandedRows[key];
                        return (
                          <React.Fragment key={key}>
                            <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`pj-hours-row-${r.name}`}>
                              <td className="px-3 py-2 font-semibold text-gray-800">
                                {r.name}
                                {r.pj_member && <span className="ml-1.5 px-1.5 py-0.5 bg-[#B08A3E]/10 text-[#B08A3E] text-[9px] font-bold rounded uppercase">PJ</span>}
                                {r.no_tracker_account && <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded">no-tracker</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-500">{r.email || "—"}</td>
                              <td className="px-3 py-2 text-gray-500 uppercase text-[10px]">{r.payment_type || "—"}</td>
                              <td className="px-3 py-2 text-gray-700 text-right">{tot.days_worked || 0}</td>
                              <td className="px-3 py-2 text-right text-[#2F5D8A] font-medium">{(tot.login_hours || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right text-gray-600">{(tot.lunch_hours || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right text-gray-600">{(tot.pause_hours || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right text-gray-800 font-bold">{(tot.paid_hours || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right">
                                {tot.open_sessions > 0 ? (
                                  <span className="px-2 py-0.5 bg-red-50 border border-red-200 text-red-600 text-[10px] font-bold rounded" data-testid={`pj-open-warn-${r.name}`}>
                                    ⚠ {tot.open_sessions} missing clock-out
                                  </span>
                                ) : (
                                  <span className="text-gray-300 text-[10px]">all closed</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {(r.records || []).length > 0 && (
                                  <button onClick={() => setExpandedRows(s => ({ ...s, [key]: !s[key] }))}
                                    className="text-[#2F5D8A] hover:underline text-[10px] font-medium" data-testid={`pj-expand-${r.name}`}>
                                    {isOpen ? "Hide" : "Show"} {r.records.length} day{r.records.length > 1 ? "s" : ""}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-50">
                                <td colSpan={10} className="px-3 py-2">
                                  <table className="w-full text-[11px]">
                                    <thead><tr className="border-b border-gray-200 text-gray-400">
                                      <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">Date</th>
                                      <th className="px-2 py-1 text-left">Clock In</th>
                                      <th className="px-2 py-1 text-left">Clock Out</th>
                                      <th className="px-2 py-1 text-left">Lunch Out</th>
                                      <th className="px-2 py-1 text-left">Lunch Return</th>
                                      <th className="px-2 py-1 text-right">Login h</th>
                                      <th className="px-2 py-1 text-right">Lunch h</th>
                                      <th className="px-2 py-1 text-right">Pause h</th>
                                      <th className="px-2 py-1 text-right">Paid h</th>
                                      <th className="px-2 py-1 text-center">Status</th>
                                    </tr></thead>
                                    <tbody>
                                      {r.records.map((day, di) => {
                                        const fmt = (s) => s ? new Date(s).toLocaleString([], { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
                                        return (
                                          <tr key={di} className={`border-b border-gray-100 ${day.open_session ? "bg-red-50/50" : ""}`} data-testid={`pj-day-${r.name}-${day.work_date}`}>
                                            <td className="px-2 py-1 font-medium text-gray-700">{day.work_date}</td>
                                            <td className="px-2 py-1 text-gray-600">{fmt(day.clock_in)}</td>
                                            <td className={`px-2 py-1 ${day.open_session ? "text-red-600 font-semibold" : "text-gray-600"}`}>
                                              {day.clock_out ? fmt(day.clock_out) : "MISSING"}
                                            </td>
                                            <td className="px-2 py-1 text-gray-500">{fmt(day.lunch_out)}</td>
                                            <td className="px-2 py-1 text-gray-500">{fmt(day.lunch_return)}</td>
                                            <td className="px-2 py-1 text-right text-gray-700">{((day.total_login_minutes || 0) / 60).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-right text-gray-500">{((day.total_lunch_minutes || 0) / 60).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-right text-gray-500">{((day.total_pause_minutes || 0) / 60).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-right font-semibold text-gray-800">{((day.total_paid_minutes || 0) / 60).toFixed(2)}</td>
                                            <td className="px-2 py-1 text-center">
                                              {day.open_session
                                                ? <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-bold rounded">OPEN — clock-out missing</span>
                                                : <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded">CLOSED</span>}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div className="px-3 py-2 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-200">
                  Display only — no payment calculation is performed from these hours. Source: <code>db.office_time_logs</code>. Period: {hoursData?.start} → {hoursData?.end}.
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};


const ReconciliationModule = () => {
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weeks] = useState(() => generateWeekPeriods(6));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [filterInspector, setFilterInspector] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [tblSearch, setTblSearch] = useState("");

  useEffect(() => { if (weeks.length > 0 && !selectedWeek) setSelectedWeek(weeks[0]); }, [weeks, selectedWeek]);

  const fetchRecon = useCallback(async () => {
    if (!selectedWeek) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/reconciliation-week`, { params: { week_start: selectedWeek.start, week_end: selectedWeek.end } });
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [selectedWeek]);

  useEffect(() => { fetchRecon(); setActiveCard(null); setFilterStatus(""); setFilterInspector(""); setFilterDate(""); setTblSearch(""); }, [fetchRecon]);

  const s = data?.summary || {};

  const inspectorList = useMemo(() => {
    if (!data?.all_orders) return [];
    return [...new Set(data.all_orders.map(o => o.inspector).filter(Boolean))].sort();
  }, [data]);

  const dateList = useMemo(() => {
    if (!data?.all_orders) return [];
    return [...new Set(data.all_orders.map(o => o.route_date).filter(Boolean))].sort();
  }, [data]);

  const filteredOrders = useMemo(() => {
    if (!data) return [];
    let list = data.all_orders || [];
    if (activeCard === "all") list = data.all_orders || [];
    else if (activeCard === "matched") list = data.matched || [];
    else if (activeCard === "orders_only") list = data.orders_only || [];
    else if (activeCard === "safeguard_only") list = data.safeguard_only || [];
    else if (activeCard === "needs_review") list = data.needs_review || [];
    else if (activeCard === "safeguard_total") list = [...(data.matched || []), ...(data.safeguard_only || [])].filter((v,i,a) => a.findIndex(x=>x.order_number===v.order_number)===i);
    if (filterStatus) list = list.filter(o => o.status === filterStatus);
    if (filterInspector) list = list.filter(o => o.inspector === filterInspector);
    if (filterDate) list = list.filter(o => o.route_date === filterDate);
    if (tblSearch) {
      const q = tblSearch.toLowerCase();
      list = list.filter(o => o.order_number?.toLowerCase().includes(q) || o.inspector?.toLowerCase().includes(q) || o.address?.toLowerCase().includes(q) || o.city?.toLowerCase().includes(q));
    }
    return list;
  }, [data, activeCard, filterStatus, filterInspector, filterDate, tblSearch]);

  const clickCard = (key) => {
    setActiveCard(activeCard === key ? null : key);
    setFilterStatus(""); setFilterInspector(""); setFilterDate(""); setTblSearch("");
  };

  const cards = [
    { key: "all", label: "Total Internal Orders", value: s.orders_week_total || 0, color: "#1F2937", sub: "Safeguard closings this week" },
    { key: "safeguard_total", label: "Total Safeguard Orders", value: s.safeguard_file_total || 0, color: "#2F5D8A", sub: "From Safeguard file" },
    { key: "matched", label: "Matched", value: s.matched || 0, color: "#6B8F62", sub: "Exists in both systems" },
    { key: "orders_only", label: "System Only", value: s.in_orders_only || 0, color: "#D1A054", sub: "Not launched in Safeguard" },
    { key: "safeguard_only", label: "Safeguard Only", value: s.in_safeguard_only || 0, color: "#E0752F", sub: "Missing in our system" },
    { key: "needs_review", label: "Needs Review", value: s.needs_review || 0, color: "#C07070", sub: "Duplicates / formatting" },
  ];

  const hasFilters = filterInspector || filterDate || filterStatus || tblSearch;

  return (
    <div className="space-y-4" data-testid="reconciliation-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Reconciliation — Order Control Tower</p>
          <p className="text-gray-400 text-xs mt-0.5">Did we finish it? Did Safeguard receive it? (Order-match only — no payment values. National excluded.)</p>
        </div>
        <button onClick={fetchRecon} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <WeekSelector weeks={weeks} selected={selectedWeek} onSelect={setSelectedWeek} color="#2F5D8A" />

      {loading ? <Spinner /> : !data ? (
        <div className="text-center py-10 text-gray-400 text-sm">Select a closing week to view reconciliation.</div>
      ) : (
        <>
          {/* 6 Summary Cards — ALL clickable */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="recon-cards">
            {cards.map(c => (
              <button key={c.key} onClick={() => clickCard(c.key)}
                className={`bg-white rounded-lg border p-3 text-left transition-all cursor-pointer hover:shadow-md ${activeCard === c.key ? "ring-2 ring-offset-1" : "border-gray-200"}`}
                style={activeCard === c.key ? { borderColor: c.color, ringColor: c.color } : {}}
                data-testid={`recon-card-${c.key}`}
              >
                <p className="text-[9px] uppercase font-semibold tracking-wider" style={{ color: c.color }}>{c.label}</p>
                <p className="text-2xl font-bold mt-0.5" style={{ color: c.color }}>{c.value}</p>
                <p className="text-[9px] text-gray-400 mt-0.5">{c.sub}</p>
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg p-2 border border-gray-200" data-testid="recon-filters">
            <Search className="h-3.5 w-3.5 text-gray-400" />
            <input type="text" value={tblSearch} onChange={e => setTblSearch(e.target.value)} placeholder="Search order, inspector, address..."
              className="px-2 py-1 border border-gray-200 rounded text-[10px] w-48 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="recon-search" />
            <select value={filterInspector} onChange={e => setFilterInspector(e.target.value)}
              className="px-2 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="recon-filter-inspector">
              <option value="">All Inspectors</option>
              {inspectorList.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <select value={filterDate} onChange={e => setFilterDate(e.target.value)}
              className="px-2 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="recon-filter-date">
              <option value="">All Dates</option>
              {dateList.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-2 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-[#2F5D8A] bg-white" data-testid="recon-filter-status">
              <option value="">All Statuses</option>
              <option value="Matched">Matched</option>
              <option value="System Only">System Only</option>
              <option value="Safeguard Only">Safeguard Only</option>
              <option value="Needs Review">Needs Review</option>
            </select>
            <span className="text-[10px] text-gray-400 ml-auto">{filteredOrders.length} orders</span>
            {hasFilters && <button onClick={() => { setFilterInspector(""); setFilterDate(""); setFilterStatus(""); setTblSearch(""); }} className="text-[10px] text-[#C07070] hover:underline">Clear</button>}
          </div>

          {/* Full Data Table — Order-match only (no money) */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="recon-table">
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 z-10"><tr className="border-b border-gray-200">
                  <TH>Order #</TH>
                  <TH>Inspector</TH>
                  <TH>Completion Date</TH>
                  <TH>Launch Date</TH>
                  <TH>Company</TH>
                  <TH>Status</TH>
                  <TH>Notes</TH>
                </tr></thead>
                <tbody>{filteredOrders.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No orders match the current filters.</td></tr>
                ) : filteredOrders.map((o, i) => {
                  const palette = {
                    "Matched": { bg: "#6B8F62", label: "MATCHED" },
                    "System Only": { bg: "#D1A054", label: "SYSTEM ONLY" },
                    "Safeguard Only": { bg: "#E0752F", label: "SAFEGUARD ONLY" },
                    "Needs Review": { bg: "#C07070", label: "NEEDS REVIEW" },
                  };
                  const p = palette[o.status] || { bg: "#6B7280", label: o.status?.toUpperCase() || "-" };
                  return (
                    <tr key={`${o.order_number}-${i}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`recon-row-${o.order_number}`}>
                      <td className="px-2 py-2 font-medium text-gray-800">{o.order_number}</td>
                      <td className="px-2 py-2 text-gray-500 text-[10px] max-w-[140px] truncate" title={o.inspector}>{o.inspector || <span className="text-gray-300 italic">not assigned</span>}</td>
                      <td className="px-2 py-2 text-gray-500 text-[10px]">{o.completion_date || <span className="text-gray-300">—</span>}</td>
                      <td className="px-2 py-2 text-gray-500 text-[10px]">{o.launch_date || <span className="text-gray-300">—</span>}</td>
                      <td className="px-2 py-2 text-gray-500 text-[10px]">{o.company || "Safeguard"}</td>
                      <td className="px-2 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold text-white whitespace-nowrap" style={{ backgroundColor: p.bg }} data-testid={`recon-status-${o.order_number}`}>
                          {p.label}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-gray-400 text-[10px] italic max-w-[200px] truncate" title={o.notes}>{o.notes || "-"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   PAYMENTS MODULE — Check Matching per Closing Week
   ════════════════════════════════════════════════════════ */
const PaymentsModule = () => {
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weeks] = useState(() => generateWeekPeriods(6));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => { if (weeks.length > 0 && !selectedWeek) setSelectedWeek(weeks[0]); }, [weeks, selectedWeek]);

  const fetchPayments = useCallback(async () => {
    if (!selectedWeek) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/weekly-payments`, { params: { week_start: selectedWeek.start, week_end: selectedWeek.end } });
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [selectedWeek]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const s = data?.summary || {};
  const orders = useMemo(() => {
    if (!data?.orders) return [];
    let filtered = data.orders;
    if (filter !== "all") {
      const map = { not_paid: "Not Paid", partially_paid: "Partially Paid", fully_paid: "Fully Paid", overpaid: "Overpaid" };
      filtered = filtered.filter(o => o.payment_status === map[filter]);
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(o => o.order_number?.toLowerCase().includes(q) || o.inspector?.toLowerCase().includes(q) || o.check_numbers?.some(c => c.toLowerCase().includes(q)));
    }
    return filtered;
  }, [data, filter, search]);

  return (
    <div className="space-y-4" data-testid="payments-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Payments — Check Matching</p>
          <p className="text-gray-400 text-xs mt-0.5">Accumulated check payments for orders in selected closing week.</p>
        </div>
        <button onClick={fetchPayments} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <WeekSelector weeks={weeks} selected={selectedWeek} onSelect={setSelectedWeek} color="#6B8F62" />

      {loading ? <Spinner /> : !data ? (
        <div className="text-center py-10 text-gray-400 text-sm">Select a closing week to view payment status.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="payments-summary">
            <div className="bg-white rounded-lg border border-[#6B8F62]/30 p-3">
              <p className="text-[10px] uppercase text-[#6B8F62] font-semibold">Fully Paid</p>
              <p className="text-xl font-bold text-[#6B8F62]">{s.fully_paid || 0}</p>
            </div>
            <div className="bg-white rounded-lg border border-[#D1A054]/30 p-3">
              <p className="text-[10px] uppercase text-[#B08A3E] font-semibold">Partially Paid</p>
              <p className="text-xl font-bold text-[#B08A3E]">{s.partially_paid || 0}</p>
            </div>
            <div className="bg-white rounded-lg border border-[#C07070]/30 p-3">
              <p className="text-[10px] uppercase text-[#8B4545] font-semibold">Not Paid</p>
              <p className="text-xl font-bold text-[#8B4545]">{s.not_paid || 0}</p>
            </div>
            <div className="bg-white rounded-lg border border-[#6B5B8D]/30 p-3">
              <p className="text-[10px] uppercase text-[#6B5B8D] font-semibold">Overpaid</p>
              <p className="text-xl font-bold text-[#6B5B8D]">{s.overpaid || 0}</p>
            </div>
            <div className="bg-white rounded-lg border border-[#2F5D8A]/30 p-3">
              <p className="text-[10px] uppercase text-[#2F5D8A] font-semibold">Total Received</p>
              <p className="text-lg font-bold text-[#2F5D8A]">${(s.total_received || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Total Expected</p>
              <p className="text-lg font-bold text-gray-700">${(s.total_expected || 0).toFixed(2)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {[{k:"all",l:"All"},{k:"fully_paid",l:"Fully Paid"},{k:"partially_paid",l:"Partial"},{k:"not_paid",l:"Not Paid"},{k:"overpaid",l:"Overpaid"}].map(f => (
              <button key={f.k} onClick={() => setFilter(f.k)}
                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${filter === f.k ? "bg-[#D1A054] text-[#1F2937]" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                data-testid={`pay-filter-${f.k}`}
              >{f.l}</button>
            ))}
            <div className="ml-auto flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order, inspector..."
                className="px-2.5 py-1 border border-gray-200 rounded text-xs w-44 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]" data-testid="payments-search" />
            </div>
            <span className="text-xs text-gray-400">{orders.length} orders</span>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="payments-table">
            <div className="max-h-[450px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 z-10"><tr className="border-b border-gray-200">
                  <TH>Order #</TH><TH>Inspector</TH><TH align="right">Received</TH>
                  <TH align="right">Expected</TH><TH align="right">Diff</TH><TH>Checks</TH><TH>Status</TH>
                </tr></thead>
                <tbody>{orders.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No orders match filters</td></tr>
                ) : orders.map(o => (
                  <tr key={o.order_number} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{o.order_number}</td>
                    <td className="px-3 py-2 text-gray-500">{o.inspector}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#2F5D8A]">${o.total_received.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{o.expected > 0 ? `$${o.expected.toFixed(2)}` : "-"}</td>
                    <td className={`px-3 py-2 text-right font-medium ${o.difference === null ? "text-gray-300" : o.difference < -0.01 ? "text-[#C07070]" : o.difference > 0.01 ? "text-[#6B5B8D]" : "text-[#6B8F62]"}`}>
                      {o.difference !== null ? `${o.difference >= 0 ? "+" : ""}$${o.difference.toFixed(2)}` : "-"}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-gray-500">{o.check_count}x</span>
                      {o.check_numbers?.length > 0 && <span className="ml-1 text-[9px] text-[#2F5D8A]">{o.check_numbers.join(", ")}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        o.payment_status === "Fully Paid" ? "bg-[#6B8F62]/15 text-[#6B8F62] border-[#6B8F62]/30" :
                        o.payment_status === "Partially Paid" ? "bg-[#D1A054]/15 text-[#B08A3E] border-[#D1A054]/30" :
                        o.payment_status === "Not Paid" ? "bg-[#C07070]/15 text-[#8B4545] border-[#C07070]/30" :
                        o.payment_status === "Overpaid" ? "bg-[#6B5B8D]/15 text-[#6B5B8D] border-[#6B5B8D]/30" :
                        "bg-gray-100 text-gray-500 border-gray-200"
                      }`}>{o.payment_status}</span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ZIP_PREFIX_MAP = {
  "AIRI28": "028, 029 (Rhode Island)",
  "AAMA21": "021",
  "AAMA26": "026",
  "AAMA15": "013-017, 020, 023-025",
  "AAMA27": "027",
};

const PaymentRatesSection = ({ rates, onRefresh, setMsg }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ regular: "", internal: "", external: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const safeguardRates = rates.filter(r => r.company !== "National");

  const startEdit = (r) => { setForm({ regular: String(r.regular ?? ""), internal: String(r.internal ?? ""), external: String(r.external ?? ""), notes: r.notes || "" }); setEditing(r.id); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.regular !== "") payload.regular = parseFloat(form.regular);
      if (form.internal !== "") payload.internal = parseFloat(form.internal);
      if (form.external !== "") payload.external = parseFloat(form.external);
      if (form.notes !== undefined) payload.notes = form.notes;
      await axios.put(`${API}/finance/payment-rates/${editing}`, payload);
      setMsg({ type: "success", text: "Rate updated" }); setEditing(null); onRefresh();
    } catch (err) { setMsg({ type: "error", text: err.response?.data?.detail || "Failed" }); }
    finally { setSaving(false); }
  };

  const recalculate = async () => {
    try { const res = await axios.post(`${API}/finance/orders/recalculate-payments`); setMsg({ type: "success", text: `Updated ${res.data.updated} orders, ${res.data.flagged} flagged` }); }
    catch (err) { setMsg({ type: "error", text: err.response?.data?.detail || "Failed" }); }
  };

  return (
    <div className="space-y-3" data-testid="payment-rates-section">
      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-xs">Code-based rates: Payment Code (from ZIP prefix) + Flow Type</p>
        <button onClick={recalculate} className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="recalculate-payments-btn"><RefreshCw className="h-3.5 w-3.5" /> Recalculate Orders</button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {safeguardRates.length === 0 ? <div className="text-center py-10 text-gray-400 text-sm">No payment codes configured.</div> : (
          <table className="w-full text-sm" data-testid="payment-rates-table">
            <thead><tr className="border-b border-gray-200"><TH>Code</TH><TH>State</TH><TH>ZIP Prefixes</TH><TH align="right">Regular</TH><TH align="right">Internal</TH><TH align="right">External</TH><TH>Notes</TH><TH align="right">Actions</TH></tr></thead>
            <tbody>{safeguardRates.map(r => (
              <tr key={r.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`rate-row-${r.code}`}>
                <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded text-xs font-bold bg-[#2F5D8A]/10 text-[#2F5D8A]">{r.code}</span></td>
                <td className="px-4 py-2.5 text-gray-600 text-xs">{r.state}</td>
                <td className="px-4 py-2.5 text-gray-400 text-[10px]">{ZIP_PREFIX_MAP[r.code] || "—"}</td>
                {editing === r.id ? (<>
                  <td className="px-4 py-2.5"><input value={form.regular} onChange={e => setForm(p => ({...p, regular: e.target.value}))} className="w-16 px-2 py-1 border rounded text-xs text-right" type="number" step="0.01" data-testid={`edit-regular-${r.code}`} /></td>
                  <td className="px-4 py-2.5"><input value={form.internal} onChange={e => setForm(p => ({...p, internal: e.target.value}))} className="w-16 px-2 py-1 border rounded text-xs text-right" type="number" step="0.01" data-testid={`edit-internal-${r.code}`} /></td>
                  <td className="px-4 py-2.5"><input value={form.external} onChange={e => setForm(p => ({...p, external: e.target.value}))} className="w-16 px-2 py-1 border rounded text-xs text-right" type="number" step="0.01" data-testid={`edit-external-${r.code}`} /></td>
                  <td className="px-4 py-2.5"><input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="w-24 px-2 py-1 border rounded text-xs" data-testid={`edit-notes-${r.code}`} /></td>
                  <td className="px-4 py-2.5 text-right"><div className="flex items-center justify-end gap-1">
                    <button onClick={handleSave} disabled={saving} className="p-1.5 bg-[#6B8F62] text-white rounded" data-testid={`save-rate-${r.code}`}><Save className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEditing(null)} className="p-1.5 bg-gray-200 text-gray-500 rounded"><X className="h-3.5 w-3.5" /></button>
                  </div></td>
                </>) : (<>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${parseFloat(r.regular).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${parseFloat(r.internal).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${parseFloat(r.external).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{r.notes || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-right"><button onClick={() => startEdit(r)} className="p-1.5 rounded hover:bg-gray-200 text-gray-500" data-testid={`edit-rate-${r.code}`}><Edit2 className="h-3.5 w-3.5" /></button></td>
                </>)}
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};


/* ════════════════════════════════════════════════════════
   INSPECTOR RATES MODULE — Angela 2026-06-02
   Per-inspector pay rates: Regular, Internal, External, National
   (with global fallback), plus Special Rule + Fixed-Day + Notes.
   Recalculate is READ-ONLY: previews per-inspector totals for the
   selected week using current rates. Edit limited to Admin + Finance
   server-side; UI hides the buttons for other roles via can_edit.
   ════════════════════════════════════════════════════════ */
const InspectorRatesModule = () => {
  const [data, setData] = useState({ inspectors: [], global_national_rate: 0, can_edit: false });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState(null);
  const [recalcWeek, setRecalcWeek] = useState(null);
  const [recalcResult, setRecalcResult] = useState(null);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const weeks = useMemo(() => generateWeekPeriods(6), []);
  useEffect(() => { if (!recalcWeek && weeks.length) setRecalcWeek(weeks[0]); }, [weeks, recalcWeek]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/inspector-rates`);
      setData(res.data);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to load inspector rates" });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.inspectors || []).filter(i => {
      if (activeOnly && i.active !== "yes") return false;
      if (!q) return true;
      return (i.name || "").toLowerCase().includes(q) || (i.full_name || "").toLowerCase().includes(q) || (i.email || "").toLowerCase().includes(q);
    });
  }, [data.inspectors, search, activeOnly]);

  const startEdit = (insp) => {
    setEditing({
      ...insp,
      regular_rate: insp.regular_rate ?? "",
      internal_rate: insp.internal_rate ?? "",
      external_rate: insp.external_rate ?? "",
      national_rate: insp.national_rate ?? "",
      special_rate_note: insp.special_rate_note || "",
      notes: insp.notes || "",
      fixed_day_enabled: !!insp.fixed_day_rule,
      fixed_day_rule: insp.fixed_day_rule || { day_of_week: "Monday", fixed_amount: "", max_orders: "", applies_to: "" },
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const payload = {
      regular_rate: editing.regular_rate === "" ? 0 : parseFloat(editing.regular_rate),
      internal_rate: editing.internal_rate === "" ? 0 : parseFloat(editing.internal_rate),
      external_rate: editing.external_rate === "" ? 0 : parseFloat(editing.external_rate),
      national_rate: editing.national_rate === "" ? "" : parseFloat(editing.national_rate),
      special_rate_note: editing.special_rate_note || "",
      notes: editing.notes || "",
      fixed_day_rule: editing.fixed_day_enabled ? {
        day_of_week: editing.fixed_day_rule.day_of_week || "Monday",
        fixed_amount: parseFloat(editing.fixed_day_rule.fixed_amount || 0),
        max_orders: parseInt(editing.fixed_day_rule.max_orders || 0, 10),
        applies_to: editing.fixed_day_rule.applies_to || "",
      } : null,
    };
    try {
      await axios.put(`${API}/finance/inspector-rates/${editing.id}`, payload);
      setMessage({ type: "success", text: `Updated rates for ${editing.name}` });
      setEditing(null);
      fetchAll();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save" });
    }
  };

  const runRecalculate = async () => {
    if (!recalcWeek) return;
    setRecalcLoading(true);
    setRecalcResult(null);
    try {
      const res = await axios.post(`${API}/finance/inspector-rates/recalculate`, {
        week_start: recalcWeek.start, week_end: recalcWeek.end,
      });
      setRecalcResult(res.data);
      setMessage({ type: "success", text: `Recalculated ${res.data.total_orders} orders for ${res.data.rows.length} inspectors` });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Recalculate failed" });
    } finally {
      setRecalcLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="inspector-rates-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Inspector Payment Rates</p>
          <p className="text-gray-400 text-xs mt-0.5">Rates AARIFI pays each inspector per order. National defaults to <span className="font-semibold text-[#6B5B8D]">${(data.global_national_rate || 0).toFixed(2)}</span> unless overridden per-inspector.</p>
        </div>
      </div>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}

      <div className="bg-white rounded-lg border border-[#2F5D8A]/25 p-3" data-testid="inspector-rates-recalc-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-[#2F5D8A]" />
            <p className="text-xs font-semibold text-[#2F5D8A] uppercase tracking-wider">Recalculate (selected week)</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <WeekSelector weeks={weeks} selected={recalcWeek} onSelect={setRecalcWeek} color="#2F5D8A" />
            <button
              onClick={runRecalculate}
              disabled={recalcLoading || !recalcWeek}
              className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] disabled:bg-gray-300 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
              data-testid="inspector-rates-recalc-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${recalcLoading ? "animate-spin" : ""}`} />
              {recalcLoading ? "Computing…" : "Recalculate"}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5">Read-only preview. Computes what each inspector would receive for the selected week using current rates. Does <span className="font-semibold">not</span> modify closed payroll or stored payments.</p>
        {recalcResult && (
          <div className="mt-3 border-t border-gray-100 pt-3" data-testid="inspector-rates-recalc-result">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] text-gray-500">
                Week <span className="font-semibold text-gray-700">{fmtRange(recalcResult.week_start, recalcResult.week_end)}</span> · {recalcResult.total_orders} payable orders
              </p>
              <p className="text-sm font-extrabold text-[#2F5D8A]" data-testid="inspector-rates-recalc-total">Total: ${(recalcResult.grand_total || 0).toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 rounded-md overflow-hidden border border-gray-200">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-gray-200 bg-white"><TH>Inspector</TH><TH align="right">Orders</TH><TH align="right">Regular</TH><TH align="right">Internal</TH><TH align="right">External</TH><TH align="right">National</TH><TH align="right">Total Pay</TH></tr></thead>
                <tbody>
                  {recalcResult.rows.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-5 text-gray-400">No payable orders in this week.</td></tr>
                  ) : recalcResult.rows.map(r => (
                    <tr key={r.inspector_name} className="border-b border-gray-100 hover:bg-white" data-testid={`recalc-row-${r.inspector_name}`}>
                      <td className="px-3 py-1.5 text-gray-700">
                        {r.inspector_name}
                        {r.missing_rate_orders > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded bg-[#C07070]/15 text-[#8B4545]" title="Orders priced at $0 because the inspector has no rate set">{r.missing_rate_orders} no-rate</span>}
                        {r.override_orders > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded bg-[#D1A054]/20 text-[#9A7735]" title="Orders using inspector_pay_override">{r.override_orders} override</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right text-gray-500">{r.orders}</td>
                      <td className="px-3 py-1.5 text-right text-gray-400">{r.by_basis.regular || 0}</td>
                      <td className="px-3 py-1.5 text-right text-gray-400">{r.by_basis.internal || 0}</td>
                      <td className="px-3 py-1.5 text-right text-gray-400">{r.by_basis.external || 0}</td>
                      <td className="px-3 py-1.5 text-right text-gray-400">{r.by_basis.national || 0}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-[#2F5D8A]">${(r.total_pay || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search inspector..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs focus:border-[#D1A054] focus:outline-none"
            data-testid="inspector-rates-search"
          />
        </div>
        <label className="flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs cursor-pointer">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} className="accent-[#D1A054]" data-testid="inspector-rates-active-toggle" />
          <span className="text-gray-600">Active only</span>
        </label>
        <span className="text-[10px] text-gray-400 ml-1">{filtered.length} of {(data.inspectors || []).length}</span>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="inspector-rates-table-wrap">
        {loading ? <Spinner /> : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">No inspectors match.</div>
        ) : (
          <table className="w-full text-sm" data-testid="inspector-rates-table">
            <thead><tr className="border-b border-gray-200">
              <TH>Inspector</TH>
              <TH align="right">Regular</TH>
              <TH align="right">Internal</TH>
              <TH align="right">External</TH>
              <TH align="right">National</TH>
              <TH>Special Rule</TH>
              <TH>Notes</TH>
              <TH align="right">Actions</TH>
            </tr></thead>
            <tbody>
              {filtered.map(i => (
                <tr key={i.id} className={`border-b border-gray-100 hover:bg-gray-50 ${i.active !== "yes" ? "opacity-60" : ""}`} data-testid={`inspector-rates-row-${i.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col">
                      <span className="text-gray-800 font-medium text-xs">{i.name}</span>
                      {i.active !== "yes" && <span className="text-[9px] uppercase tracking-wider text-gray-400">Inactive</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${(i.regular_rate || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${(i.internal_rate || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-800 font-medium">${(i.external_rate || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {i.national_uses_global ? (
                      <span className="text-gray-400 text-xs" title="No per-inspector override - using global National rate">
                        ${(i.effective_national_rate || 0).toFixed(2)} <span className="text-[9px] uppercase text-gray-300">global</span>
                      </span>
                    ) : (
                      <span className="text-[#6B5B8D] font-semibold text-xs">${(i.effective_national_rate || 0).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">
                    {i.fixed_day_rule ? (
                      <span className="px-1.5 py-0.5 rounded bg-[#D1A054]/15 text-[#9A7735] font-medium text-[10px]">
                        {i.fixed_day_rule.day_of_week} · ${parseFloat(i.fixed_day_rule.fixed_amount || 0).toFixed(2)}{i.fixed_day_rule.max_orders ? ` (<=${i.fixed_day_rule.max_orders})` : ""}
                      </span>
                    ) : i.special_rate_note ? (
                      <span className="text-gray-500" title={i.special_rate_note}>{i.special_rate_note.length > 40 ? i.special_rate_note.slice(0, 40) + "..." : i.special_rate_note}</span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{i.notes ? (i.notes.length > 30 ? i.notes.slice(0, 30) + "..." : i.notes) : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    {data.can_edit ? (
                      <button onClick={() => startEdit(i)} className="p-1.5 rounded hover:bg-gray-200 text-gray-500" data-testid={`edit-inspector-rate-${i.id}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    ) : <span className="text-gray-300 text-[10px]">view-only</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <InspectorRateEditDrawer
          inspector={editing}
          globalNational={data.global_national_rate}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
};

const InspectorRateEditDrawer = ({ inspector, globalNational, onChange, onClose, onSave }) => {
  const set = (patch) => onChange({ ...inspector, ...patch });
  const setRule = (patch) => onChange({ ...inspector, fixed_day_rule: { ...inspector.fixed_day_rule, ...patch } });
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="inspector-rate-edit-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider">Edit Inspector Rates</p>
            <p className="text-sm font-bold text-gray-800" data-testid="drawer-inspector-name">{inspector.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-400" data-testid="drawer-close-btn"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider mb-2">Per-Order Rates</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[9px] uppercase text-gray-400 block mb-0.5">Regular</label>
                <input type="number" step="0.01" value={inspector.regular_rate} onChange={e => set({ regular_rate: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right" data-testid="drawer-regular-rate" />
              </div>
              <div>
                <label className="text-[9px] uppercase text-gray-400 block mb-0.5">Internal</label>
                <input type="number" step="0.01" value={inspector.internal_rate} onChange={e => set({ internal_rate: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right" data-testid="drawer-internal-rate" />
              </div>
              <div>
                <label className="text-[9px] uppercase text-gray-400 block mb-0.5">External</label>
                <input type="number" step="0.01" value={inspector.external_rate} onChange={e => set({ external_rate: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right" data-testid="drawer-external-rate" />
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider mb-2">National Rate (per-inspector override)</p>
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" value={inspector.national_rate} onChange={e => set({ national_rate: e.target.value })}
                placeholder={`Empty -> use global $${(globalNational || 0).toFixed(2)}`}
                className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="drawer-national-rate" />
              {inspector.national_rate !== "" && (
                <button onClick={() => set({ national_rate: "" })} className="text-[10px] text-gray-400 hover:text-[#8B4545] underline" data-testid="drawer-clear-national-btn">Clear</button>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Leave empty to use the global National rate (${(globalNational || 0).toFixed(2)}). Set a number to override only for this inspector.</p>
          </div>

          <div>
            <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider mb-2">Special Rate Note (free text)</p>
            <textarea value={inspector.special_rate_note} onChange={e => set({ special_rate_note: e.target.value })}
              placeholder="e.g. Pays $250 flat on Mondays, includes up to 8 orders"
              rows={2} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="drawer-special-note" />
          </div>

          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={inspector.fixed_day_enabled} onChange={e => set({ fixed_day_enabled: e.target.checked })} className="accent-[#D1A054]" data-testid="drawer-fixed-day-toggle" />
              <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Fixed-Day Rule (optional structured)</span>
            </label>
            {inspector.fixed_day_enabled && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] uppercase text-gray-400 block mb-0.5">Day of Week</label>
                  <select value={inspector.fixed_day_rule.day_of_week || "Monday"} onChange={e => setRule({ day_of_week: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white" data-testid="drawer-fixed-day-dow">
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] uppercase text-gray-400 block mb-0.5">Fixed Amount ($)</label>
                  <input type="number" step="0.01" value={inspector.fixed_day_rule.fixed_amount} onChange={e => setRule({ fixed_amount: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right" data-testid="drawer-fixed-day-amount" />
                </div>
                <div>
                  <label className="text-[9px] uppercase text-gray-400 block mb-0.5">Max Orders Covered</label>
                  <input type="number" value={inspector.fixed_day_rule.max_orders} onChange={e => setRule({ max_orders: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right" data-testid="drawer-fixed-day-max" />
                </div>
                <div>
                  <label className="text-[9px] uppercase text-gray-400 block mb-0.5">Applies To (optional)</label>
                  <input value={inspector.fixed_day_rule.applies_to} onChange={e => setRule({ applies_to: e.target.value })}
                    placeholder="e.g. Internal only"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="drawer-fixed-day-applies" />
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider mb-2">Notes</p>
            <textarea value={inspector.notes} onChange={e => set({ notes: e.target.value })}
              rows={2} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="drawer-notes" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700" data-testid="drawer-cancel-btn">Cancel</button>
          <button onClick={onSave} className="px-4 py-1.5 bg-[#6B8F62] hover:bg-[#5A7D52] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="drawer-save-btn">
            <Save className="h-3.5 w-3.5" /> Save Rates
          </button>
        </div>
      </div>
    </div>
  );
};




/* ─── Shared Components ─── */
const Spinner = () => <div className="flex items-center justify-center h-24"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]" /></div>;
const TH = ({ children, align = "left" }) => <th className={`px-4 py-2.5 text-${align} text-[10px] uppercase font-semibold tracking-wider text-gray-400`}>{children}</th>;
const MsgBar = ({ message, onClose }) => (
  <div className={`p-2.5 rounded-lg flex items-center justify-between text-sm ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
    <span>{message.text}</span><button onClick={onClose}><X className="h-3.5 w-3.5" /></button>
  </div>
);
const FormSelect = ({ label, value, onChange, options, testId }) => (
  <div><label className="text-[10px] uppercase text-gray-400 font-semibold block mb-1">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none" data-testid={testId}>{options.map(o => <option key={o} value={o}>{o}</option>)}</select>
  </div>
);
const FormInput = ({ label, value, onChange, placeholder, type = "text", testId }) => (
  <div><label className="text-[10px] uppercase text-gray-400 font-semibold block mb-1">{label}</label>
    <input type={type} step={type === "number" ? "0.01" : undefined} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none" data-testid={testId} />
  </div>
);
const ActionBtns = ({ onEdit, onDelete, id, prefix }) => (
  <div className="flex items-center justify-end gap-1">
    <button onClick={onEdit} className="p-1.5 rounded hover:bg-gray-200 text-gray-500" data-testid={`edit-${prefix}-${id}`}><Edit2 className="h-3.5 w-3.5" /></button>
    <button onClick={onDelete} className="p-1.5 rounded hover:bg-[#C07070]/20 text-gray-500 hover:text-[#8B4545]" data-testid={`delete-${prefix}-${id}`}><Trash2 className="h-3.5 w-3.5" /></button>
  </div>
);
const fmtRange = (start, end) => {
  if (!start || !end) return "";
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const opts = { month: "short", day: "numeric" };
  return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}, ${e.getFullYear()}`;
};

export default FinancePage;
